import { Canvas, useFrame, useThree } from "@react-three/fiber";
import {
  AdaptiveDpr,
  Environment,
  Html,
  MeshPortalMaterial,
  Stats,
} from "@react-three/drei";
import { Bloom, EffectComposer, LensFlare, SMAA } from "@react-three/postprocessing";
import { Leva, useControls } from "leva";
import {
  isRevealComplete,
  markRevealComplete,
  onCpbLoaderHidden,
  onRevealComplete,
} from "../viewerLifecycle";
import { flushSync } from "react-dom";
import { onCpbChange } from "../cpbBridge";

/** Parameters for the LED bloom post-process, tuned via the leva panel. */
type GlowParams = {
  intensity: number;
  luminanceThreshold: number;
  luminanceSmoothing: number;
  radius: number;
  /** Multiplier on top of state.brightness for LED-material emissiveIntensity. */
  ledEmissiveBoost: number;
  /** Bloom pass render-target scale relative to the canvas size (0.1…1). */
  resolutionScale: number;
  /** Mip levels for `mipmapBlur` — higher = wider possible halo. */
  levels: number;
};

/** drei Environment presets — matched to leva's `envPreset` select values. */
type EnvPreset =
  | "studio"
  | "warehouse"
  | "city"
  | "apartment"
  | "sunset"
  | "dawn"
  | "night"
  | "forest"
  | "park"
  | "lobby";
import {
  Component,
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  CanvasTexture,
  ClampToEdgeWrapping,
  Color,
  DoubleSide,
  FrontSide,
  Group,
  InstancedBufferAttribute,
  InstancedMesh,
  LinearFilter,
  MathUtils,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  Quaternion,
  RepeatWrapping,
  SRGBColorSpace,
  TextureLoader,
  Vector3,
  Vector4,
  type BufferGeometry,
  type Texture,
} from "three";
import { registerSnapshotProvider } from "../snapshotBridge";
import {
  buildApertureHull,
  buildDimensions,
  buildDimensionsFromState,
  polygonBBox,
  polygonLedPointCount,
  type AperturePolygon,
  type LedPointSample,
} from "./geometry";
import {
  clearGeometryCache,
  getCachedBarTubeGeometries,
  getCachedLedPointSamples,
  getCachedPerimeterRailGeometry,
  getCachedPerimeterWallGeometry,
  getCachedPolygonShapeGeometry,
} from "./geometryCache";
import { ensureFontReady } from "./fontFamily";
import { LedPointsInstancedLayer } from "./lighting/LedPointsInstanced";
import {
  mergeBandWallGeometries,
  polygonPerimeter,
  type PerimeterArcRange,
} from "./wallTextGeometry";
import { MIRROR_VIRTUAL_DEPTH } from "./mirrorConstants";
import {
  ANIM_STYLE_CODE,
  injectBandAlpha,
  injectLedSolidGain,
  injectRgbWheelCenterShader,
  injectRgbWheelLedMeshBasic,
  type ShaderCompileParams,
} from "./rgbWheelShader";
import { PALETTE_COUNT, getPaletteLutTexture } from "./palettes";
import { ProcFrameMesh } from "./ProcFrameMesh";
import {
  DEFAULT_CENTER_DISPLAY_TEXT,
  DEFAULT_LED_TEXT_LIGHTING_PLACEHOLDER,
  getDefaultCenterLogoUrl,
} from "./centerDesignDefaults";
import { sanitizeCenterTextInput } from "./centerTextRules";
import { makeTextTexture } from "./textTexture";
import { TEXT_FONT_SIZE_REF, type ConfiguratorState, type LogoTransformState } from "./types";
import { getState, setState as storeSetState, useConfigurator } from "./configuratorStore";
import type { LightStyle } from "./types";

type WallTextChannelId = "unified" | "long" | "short";

interface WallTextChannelSource {
  id: WallTextChannelId;
  content: string;
  color: string;
  rgb: boolean;
  font: string;
  fontSize: number;
  /** null = full closed perimeter (square/circle); else just the listed arcs. */
  arcs: PerimeterArcRange[] | null;
}

interface WallTextChannelRender {
  id: WallTextChannelId;
  source: WallTextChannelSource;
  textMap: CanvasTexture;
  textMapMirrored: CanvasTexture;
  uRepeats: number;
  /** Number of depth bands originally collapsed into the merged geometries.
   *  Kept for telemetry only — the shader picks alpha from `uBandOpacities`
   *  by per-vertex `aBandIdx`, so this is not on the hot path. */
  bandCount: number;
  /** Single merged geometry of all `!mirrored` bands (UV runs forward).
   *  `null` when no such bands exist (e.g. only one mirrored band). */
  geoNonMirrored: BufferGeometry | null;
  /** Single merged geometry of all `mirrored` bands (UV `flipY`-mirrored
   *  texture so reflections aren't backwards). `null` when none exist. */
  geoMirrored: BufferGeometry | null;
}

/** Back / front margins inside the virtual cavity (m). Span uses MIRROR_VIRTUAL_DEPTH. */
const LAYER_Z_MARGIN_BACK = 0.006;
const LAYER_Z_MARGIN_FRONT = 0.014;

/**
 * Shared 1×1 plane geometry. Both the center-text and logo InstancedMesh
 * stacks scale this via per-instance matrices (or per-mesh `scale={...}`),
 * so the geometry itself is identical across both call sites — there's no
 * reason to allocate two copies. Lives for the session; never disposed.
 */
const UNIT_PLANE = new PlaneGeometry(1, 1);

/** Reflection planes only — no LED hue (LED color is on contour tubes / points). */
const DEPTH_PLANE_COLOR = "#101010";

const LAYER_Z_EPS = 0.0015;
const CENTER_ALPHA_TEST = 0.025;
const FRONT_LAYER_ALPHA_TEST = 0.5;

/**
 * Inset (fraction of one band's span in t units) of LED rails from each band
 * face. Single source of truth used by both the rail-tube placement (so two
 * adjacent bands' rails stay distinct in z) and the wall-text geometry (so
 * glyphs aren't drawn over the rails). Smaller value → rails sit closer to
 * the band boundary, leaving more depth for text inside the band.
 */
const LED_RAIL_INSET_FRAC = 0.12;
/**
 * Extra gap between a rail and the nearest text-wall edge, as a fraction of one
 * band's span. Pushes glyphs off the rails so they don't visually touch.
 */
const TEXT_RAIL_CLEARANCE_FRAC = 0.035;

/** Portal plane = former glass plane; inner scene is shifted by -PORTAL_SURFACE_Z so world Z stays unchanged. */
const PORTAL_SURFACE_Z = 0.02;

/**
 * Drei MeshPortalMaterial: with `blend > 0`, the inner useFrame either renders
 * only the portal (`blend === 1`) or does a fullscreen mix with root. In that
 * case root is rendered by a different camera and the frame disappears.
 * For a normal in-frame portal we keep `blend = 0`: content goes to FBO, while
 * the screen shows a regular scene render.
 */

function layerProgress(i: number, n: number): number {
  if (n <= 1) return 0;
  return i / (n - 1);
}

/**
 * Progress sampled at the center of each depth interval: for n layers this yields
 * `(i + 0.5) / n`, i.e. no sample lands exactly on the front glass plane.
 *
 * Used for physical LED emitters (bar/point): in real mirrors the LED strip sits
 * between front and rear mirrors, so the first visible emitter should be slightly
 * behind the portal edge, not glued to it.
 */
function layerMidProgress(i: number, n: number): number {
  if (n <= 0) return 0;
  return (i + 0.5) / n;
}

/**
 * Per-layer opacity fade for content that should "recede into infinity"
 * (center text, logo, side-light LEDs).
 *
 * Curve: classic Hermite `smoothstep` `3t² − 2t³` — an S-curve that:
 *   • stays near 1 at the front (no abrupt jump on the 2nd / 3rd layer);
 *   • drops through ~0.5 in the middle (the fade becomes clearly visible);
 *   • approaches 0 quickly near the deepest layer (real "infinity" tunnel,
 *     not a stack of equally bright copies).
 *
 * At t=1 (front):        1.0
 * Near front (t ≈ 0.93): ≈ 0.99
 * t ≈ 0.7:               ≈ 0.78
 * Mid (t = 0.5):         = 0.5
 * t ≈ 0.3:               ≈ 0.22
 * Near back (t ≈ 0.07):  ≈ 0.013
 * At t=0 (deepest):       0
 *
 * Returns 1 when there's only one layer (no fade to apply).
 */
function infinityFade(t: number, n: number): number {
  if (n <= 1) return 1;
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}

function layerOpacityAt(i: number, n: number, brightness: number): number {
  if (n <= 1) return 1;
  if (i === n - 1) return 1;
  const t = layerProgress(i, n);
  return Math.min(infinityFade(t, n) * Math.max(brightness, 0), 1);
}

/**
 * Portal-glass scale relative to the aperture hull.
 *   1.0 = glass exactly fills the frame's inner edge (no visible rim between the
 *   mirror content and the frame). Keep at 1.0: the hull already comes from the
 *   GLB's real aperture (Glass / Plane-strip / ring bbox), so we don't need an
 *   extra safety inset.
 */
const GLASS_INSET = 1.0;

/**
 * Extra push (m) applied to LED contour tubes and LED points along −Z so they
 * sit visibly behind the portal glass plane. Without this offset, when the
 * camera orbits off-axis the portal silhouette's edge (at z ≈ 0) cuts through
 * the front LED ring (also near z ≈ 0) because they share almost the same XY
 * radius; the LED ends up half-occluded by the portal boundary.
 *
 * Pushing the LEDs deeper into the cavity keeps them clearly "inside" the
 * portal at every angle — the portal edge passes in front of them rather than
 * through them.
 */
const LED_DEPTH_PUSH = 0.018;

/**
 * Separate fill factor for the center design (text/logo) in the aperture plane.
 * It controls the visible gap between the LED rings (`scale = 1.0` at aperture)
 * and the center text/logo. 0.85 is about 7.5% margin on each side.
 */
const CENTER_DESIGN_FILL = 0.85;
/**
 * Canvas px per meter of aperture width `w` — wrap width tracks mirror opening (IM-004).
 * Lower values yield a **narrower** wrap column → smaller texture `cw`/`ch` → larger glyphs
 * on the same world quad (closer to Shopify 2D product preview at equal `textFontSize`).
 */
const TEXT_WRAP_PX_PER_METER = 150;
/**
 * 3D-only "zoom" for center text: `1` = default. Larger → smaller base plane (`hole/s`) then
 * mesh scale up toward the aperture (capped so `pw×scale` stays inside LED margin).
 * Does not change CPB / `textFontSize`. Clamped to [1, 4] to avoid absurd editor values
 * (e.g. 20) blowing up mesh scale / pixelation.
 */
const CENTER_TEXT_PREVIEW_XY_SCALE = 2;
function clampCenterTextPreviewScale(): number {
  return Math.max(1, Math.min(4, CENTER_TEXT_PREVIEW_XY_SCALE));
}
/**
 * Reference mirror size in inches for world-space text bounds (`buildDimensions`).
 * When `textFontSize === TEXT_FONT_SIZE_REF` (default 45px), changing mirror
 * **Size** does not change this field while aperture is still large enough.
 * Larger font scales this field (`fontScale`) until it reaches aperture limits
 * (`min` of `holeW/H`).
 */
const TEXT_LAYOUT_REF_INCH = 36;

/**
 * t=0 → deepest layer (smallest), t=1 → frontmost layer.
 * Front ring sits flush with the frame's inner aperture (scale = 1.0) so the
 * LED tubes (and the depth/cavity stack) hug the frame — no visible gap
 * between the gold rim and the front LED ring, like a real infinity mirror.
 * Rings still shrink as they recede into the depth (0.94 → 1.0).
 */
function layerScale(t: number): number {
  return 0.94 + t * 0.06;
}

function layerZPos(t: number): number {
  const zBack = -MIRROR_VIRTUAL_DEPTH + LAYER_Z_MARGIN_BACK;
  const zFront = LAYER_Z_MARGIN_FRONT;
  return zBack + t * (zFront - zBack);
}

function logoPlaneSize(
  texture: { image: HTMLImageElement | HTMLCanvasElement | OffscreenCanvas },
  maxW: number,
  maxH: number,
): { pw: number; ph: number } {
  const img = texture.image;
  const iw =
    ("naturalWidth" in img && img.naturalWidth) || ("width" in img && img.width) || 1;
  const ih =
    ("naturalHeight" in img && img.naturalHeight) || ("height" in img && img.height) || 1;
  const aspect = iw / ih;
  let pw = maxW;
  let ph = maxH;
  if (aspect > maxW / maxH) {
    ph = maxW / aspect;
  } else {
    pw = maxH * aspect;
  }
  return { pw, ph };
}

/**
 * Cache-bust remote URLs when CPB bumps `assetRevision` with a stable `https?` URL.
 * Never append query/hash to `blob:` or `data:` — those schemes do not accept arbitrary
 * suffixes.
 */
function makeRevisionedTextureUrl(url: string, revision: string): string {
  if (!revision) return url;
  if (url.startsWith("blob:") || url.startsWith("data:")) return url;
  // CPB private-image URLs can be signed/guarded; appending arbitrary query
  // params (like `rev`) may invalidate access for some assets (notably SVG).
  if (url.includes("/private-image/")) return url;
  return `${url}${url.includes("?") ? "&" : "?"}rev=${encodeURIComponent(revision)}`;
}

function useLogoTexture(textureUrl: string): Texture | null {
  const [texture, setTexture] = useState<Texture | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrlToRevoke: string | null = null;

    const assignTexture = (next: Texture) => {
      if (cancelled) {
        next.dispose();
        return;
      }
      setTexture((prev) => {
        if (prev && prev !== next) prev.dispose();
        return next;
      });
    };

    const loader = new TextureLoader();
    loader.setCrossOrigin("anonymous");
    loader.load(
      textureUrl,
      (loaded) => assignTexture(loaded),
      undefined,
      () => {
        fetch(textureUrl, { mode: "cors", credentials: "omit" })
          .then((r) => {
            if (!r.ok) throw new Error(`HTTP_${r.status}`);
            return r.blob();
          })
          .then((blob) => {
            if (cancelled) return;
            const prepareObjectUrl = async (): Promise<string> => {
              if (blob.type === "image/svg+xml") return URL.createObjectURL(blob);
              if (blob.type === "image/svg" || textureUrl.toLowerCase().includes(".svg")) {
                const svgText = await blob.text();
                const normalized = new Blob([svgText], {
                  type: "image/svg+xml;charset=utf-8",
                });
                return URL.createObjectURL(normalized);
              }
              return URL.createObjectURL(blob);
            };
            void prepareObjectUrl().then((objectUrl) => {
              objectUrlToRevoke = objectUrl;
              const imgFromBlob = new Image();
              imgFromBlob.decoding = "async";
              imgFromBlob.onload = () => {
                const naturalW = Math.max(1, imgFromBlob.naturalWidth || imgFromBlob.width || 1);
                const naturalH = Math.max(1, imgFromBlob.naturalHeight || imgFromBlob.height || 1);
                const longEdge = Math.max(naturalW, naturalH);
                const TARGET_LONG_EDGE = 2048;
                const MAX_AXIS = 4096;
                const upscale = Math.max(1, TARGET_LONG_EDGE / longEdge);
                const w = Math.min(MAX_AXIS, Math.max(1, Math.round(naturalW * upscale)));
                const h = Math.min(MAX_AXIS, Math.max(1, Math.round(naturalH * upscale)));
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                  URL.revokeObjectURL(objectUrl);
                  objectUrlToRevoke = null;
                  return;
                }
                ctx.clearRect(0, 0, w, h);
                ctx.drawImage(imgFromBlob, 0, 0, w, h);
                assignTexture(new CanvasTexture(canvas));
                URL.revokeObjectURL(objectUrl);
                objectUrlToRevoke = null;
              };
              imgFromBlob.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                objectUrlToRevoke = null;
              };
              imgFromBlob.src = objectUrl;
            });
          })
          .catch(() => {
            // Keep null; logo can be retried by changing source/revision.
          });
      },
    );

    return () => {
      cancelled = true;
      if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
      setTexture((prev) => {
        if (prev) prev.dispose();
        return null;
      });
    };
  }, [textureUrl]);

  return texture;
}

/** Same logo texture on every infinity depth layer, scaled per layer */
function LogoImageLayers({
  url,
  revision,
  w,
  h,
  depthLayers,
  brightness,
  logoRgb,
  logoTransform,
  centerContrast,
  onBeforeCompileCenter,
}: {
  url: string;
  revision: string;
  w: number;
  h: number;
  depthLayers: number;
  brightness: number;
  logoRgb: boolean;
  logoTransform: LogoTransformState;
  centerContrast: "none" | "light" | "dark";
  onBeforeCompileCenter: (shader: ShaderCompileParams) => void;
}) {
  const textureUrl = useMemo(() => makeRevisionedTextureUrl(url, revision), [url, revision]);
  const texture = useLogoTexture(textureUrl);
  const { gl } = useThree();

  /**
   * "Add contrast?" outline around the logo silhouette. Built by stamping a
   * tinted (white or black) copy of the logo at 16 angular offsets around
   * each opaque pixel, then drawing the original image on top — the offset
   * stamps poke out beyond the alpha edge by `haloPx` and read as a clean
   * halo. The original `texture` stays untouched (RGB shader / mip pipeline);
   * `displayTexture` is the version actually rendered on the meshes.
   */
  const displayTexture = useMemo(() => {
    if (!texture || centerContrast === "none") return texture;
    const img = texture.image as
      | (CanvasImageSource & { width: number; height: number })
      | undefined;
    if (!img || !img.width || !img.height) return texture;

    const haloColor = centerContrast === "dark" ? "#000000" : "#ffffff";
    /**
     * Halo around the logo's alpha silhouette. Scales lightly with image
     * size so a 256‑px upload still gets a visible halo and a 4096‑px upload
     * doesn't end up with a chunky frame; clamped between the previous
     * "too thin" (1‑2 px) and "too heavy" (`0.025 * min`, ~13 px on 512)
     * values so the contrast reads cleanly without dominating the logo.
     */
    const haloPx = Math.max(
      3,
      Math.min(6, Math.round(Math.min(img.width, img.height) * 0.012)),
    );

    const out = document.createElement("canvas");
    out.width = img.width + haloPx * 2;
    out.height = img.height + haloPx * 2;
    const ctx = out.getContext("2d");
    if (!ctx) return texture;

    // Pre-tint the silhouette so we can stamp it cheaply at multiple offsets
    // (`source-in` keeps the original alpha shape, but replaces RGB with
    // the halo colour).
    const tinted = document.createElement("canvas");
    tinted.width = img.width;
    tinted.height = img.height;
    const tctx = tinted.getContext("2d");
    if (!tctx) return texture;
    tctx.drawImage(img as CanvasImageSource, 0, 0);
    tctx.globalCompositeOperation = "source-in";
    tctx.fillStyle = haloColor;
    tctx.fillRect(0, 0, img.width, img.height);

    const offsets = 16;
    for (let i = 0; i < offsets; i++) {
      const a = (i / offsets) * Math.PI * 2;
      ctx.drawImage(
        tinted,
        haloPx + Math.cos(a) * haloPx,
        haloPx + Math.sin(a) * haloPx,
      );
    }
    ctx.drawImage(img as CanvasImageSource, haloPx, haloPx);

    const tex = new CanvasTexture(out);
    tex.colorSpace = SRGBColorSpace;
    return tex;
  }, [texture, centerContrast]);

  // Dispose the derived texture (only when it was actually built — never the
  // original `texture`, that has its own lifecycle inside `useLogoTexture`).
  useEffect(() => {
    return () => {
      if (displayTexture && displayTexture !== texture) displayTexture.dispose();
    };
  }, [displayTexture, texture]);

  useLayoutEffect(() => {
    if (!displayTexture) return;
    displayTexture.colorSpace = SRGBColorSpace;
    displayTexture.wrapS = ClampToEdgeWrapping;
    displayTexture.wrapT = ClampToEdgeWrapping;
    displayTexture.generateMipmaps = true;
    displayTexture.minFilter = LinearFilter;
    displayTexture.magFilter = LinearFilter;
    const maxA = gl.capabilities.getMaxAnisotropy?.() ?? 1;
    displayTexture.anisotropy = Math.min(16, maxA);
    /** Pan/zoom is done in world space via `group` (plane), not through UVs. */
    displayTexture.repeat.set(1, 1);
    displayTexture.offset.set(0, 0);
    displayTexture.center.set(0.5, 0.5);
    displayTexture.rotation = 0;
    displayTexture.needsUpdate = true;
  }, [displayTexture, gl]);

  const layerSizes = useMemo(() => {
    if (!displayTexture) return [] as { pw: number; ph: number }[];
    return Array.from({ length: depthLayers }, (_, i) => {
      const t = layerProgress(i, depthLayers);
      const sc = layerScale(t);
      return logoPlaneSize(displayTexture, w * sc * 0.65, h * sc * 0.65);
    });
  }, [displayTexture, w, h, depthLayers]);

  // UNIT_PLANE is shared module-wide; no per-mount allocation / dispose.
  const sharedLogoPlane = UNIT_PLANE;

  /** UI "pixel" offsets -> meters relative to aperture span (w/h), with no texture crop. */
  const moveRefPx = 1000;
  const rawPosX = (logoTransform.offsetPxX / moveRefPx) * w;
  const rawPosY = (logoTransform.offsetPxY / moveRefPx) * h;
  const s = Math.max(0.05, Math.min(6, logoTransform.scalePercent / 100));

  if (!displayTexture || layerSizes.length === 0) return null;

  // Keep the logo fully inside the front aperture so it doesn't render outside
  // the LED/frame opening when users drag it beyond the visible bounds.
  const frontSc = layerScale(1);
  const frontHoleW = w * frontSc * 0.98;
  const frontHoleH = h * frontSc * 0.98;
  const { pw: frontPw, ph: frontPh } = logoPlaneSize(displayTexture, w * frontSc * 0.65, h * frontSc * 0.65);
  const rot = MathUtils.degToRad(logoTransform.rotationDeg);
  const c = Math.abs(Math.cos(rot));
  const si = Math.abs(Math.sin(rot));
  // Axis-aligned bbox size of the rotated logo plane at unit scale.
  const rotBBoxWUnit = c * frontPw + si * frontPh;
  const rotBBoxHUnit = si * frontPw + c * frontPh;
  const scaleCapW = frontHoleW / Math.max(rotBBoxWUnit, 1e-6);
  const scaleCapH = frontHoleH / Math.max(rotBBoxHUnit, 1e-6);
  const renderScale = Math.max(0.05, Math.min(s, scaleCapW, scaleCapH));
  const logoW = rotBBoxWUnit * renderScale;
  const logoH = rotBBoxHUnit * renderScale;
  const maxPosX = Math.max(0, (frontHoleW - logoW) * 0.5);
  const maxPosY = Math.max(0, (frontHoleH - logoH) * 0.5);
  const posX = Math.max(-maxPosX, Math.min(maxPosX, rawPosX));
  const posY = Math.max(-maxPosY, Math.min(maxPosY, rawPosY));

  return (
    <group
      position={[posX, posY, 0]}
      rotation={[0, 0, MathUtils.degToRad(logoTransform.rotationDeg)]}
      scale={[logoTransform.flipH ? -renderScale : renderScale, renderScale, 1]}
    >
      <InstancedLogoBackLayers
        plane={sharedLogoPlane}
        layerSizes={layerSizes}
        depthLayers={depthLayers}
        brightness={brightness}
        displayTexture={displayTexture}
        logoRgb={logoRgb}
        onBeforeCompileCenter={onBeforeCompileCenter}
      />
      {/* Front layer — separate mesh, unique alphaTest + depthWrite. */}
      {layerSizes.length > 0 && (() => {
        const i = layerSizes.length - 1;
        const front = layerSizes[i];
        const t = layerProgress(i, depthLayers);
        const zPos = layerZPos(t);
        const opacity = layerOpacityAt(i, depthLayers, brightness);
        return (
          <mesh
            geometry={sharedLogoPlane}
            scale={[front.pw, front.ph, 1]}
            position={[0, 0, zPos + LAYER_Z_EPS]}
            renderOrder={40 + i}
          >
            <meshBasicMaterial
              key={`logo-front-${logoRgb ? "rgb" : "solid"}`}
              map={displayTexture}
              color="#ffffff"
              transparent
              opacity={opacity}
              alphaTest={FRONT_LAYER_ALPHA_TEST}
              toneMapped={false}
              depthWrite
              side={FrontSide}
              {...(logoRgb
                ? {
                  defines: { USE_RGB_WHEEL_CENTER: 1 },
                  onBeforeCompile: onBeforeCompileCenter,
                }
                : {})}
            />
          </mesh>
        );
      })()}
    </group>
  );
}

/**
 * Back N-1 logo layers in a single InstancedMesh. Per-instance matrix encodes
 * the per-layer plane size; per-instance opacity rides through an
 * InstancedBufferAttribute that the patched fragment shader multiplies into
 * the texture's alpha. Front layer stays as a regular <mesh> in the parent
 * because it has unique alphaTest + depthWrite.
 */
function InstancedLogoBackLayers({
  plane,
  layerSizes,
  depthLayers,
  brightness,
  displayTexture,
  logoRgb,
  onBeforeCompileCenter,
}: {
  plane: PlaneGeometry;
  layerSizes: { pw: number; ph: number }[];
  depthLayers: number;
  brightness: number;
  displayTexture: Texture;
  logoRgb: boolean;
  onBeforeCompileCenter: (s: ShaderCompileParams) => void;
}) {
  const meshRef = useRef<InstancedMesh | null>(null);
  const opacityAttrRef = useRef<InstancedBufferAttribute | null>(null);
  const backCount = Math.max(0, layerSizes.length - 1);

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || backCount === 0) return;
    /**
     * Same stability trick as CenterTextSection — reuse the
     * InstancedBufferAttribute so a brightness slider tick doesn't force a
     * fresh GPU buffer upload.
     */
    let attr = opacityAttrRef.current;
    if (!attr || attr.array.length !== backCount) {
      attr = new InstancedBufferAttribute(new Float32Array(backCount), 1);
      opacityAttrRef.current = attr;
      mesh.geometry.setAttribute("instanceOpacity", attr);
    } else if (mesh.geometry.getAttribute("instanceOpacity") !== attr) {
      mesh.geometry.setAttribute("instanceOpacity", attr);
    }

    const m = new Matrix4();
    const pos = new Vector3();
    const q = new Quaternion();
    const sc = new Vector3();
    const arr = attr.array as Float32Array;
    for (let i = 0; i < backCount; i++) {
      const layer = layerSizes[i];
      const t = layerProgress(i, depthLayers);
      const zPos = layerZPos(t) + LAYER_Z_EPS;
      pos.set(0, 0, zPos);
      sc.set(layer.pw, layer.ph, 1);
      m.compose(pos, q, sc);
      mesh.setMatrixAt(i, m);
      arr[i] = layerOpacityAt(i, depthLayers, brightness);
    }
    mesh.instanceMatrix.needsUpdate = true;
    attr.needsUpdate = true;
    mesh.count = backCount;
  }, [layerSizes, depthLayers, brightness, backCount, plane]);

  const compileBack = useCallback(
    (shader: ShaderCompileParams) => {
      if (logoRgb) onBeforeCompileCenter(shader);
      shader.vertexShader = shader.vertexShader
        .replace(
          "#include <common>",
          `#include <common>
           attribute float instanceOpacity;
           varying float vInstanceOpacity;`,
        )
        .replace(
          "#include <begin_vertex>",
          `#include <begin_vertex>
           vInstanceOpacity = instanceOpacity;`,
        );
      shader.fragmentShader = shader.fragmentShader
        .replace(
          "#include <common>",
          `#include <common>
           varying float vInstanceOpacity;`,
        )
        // three.js renamed `output_fragment` → `opaque_fragment` around r150;
        // we're on 0.170. Multiply alpha AFTER the chunk so our value isn't
        // overwritten by the chunk's `gl_FragColor = vec4(outgoingLight,
        // diffuseColor.a)` assignment.
        .replace(
          "#include <opaque_fragment>",
          `#include <opaque_fragment>
           gl_FragColor.a *= vInstanceOpacity;`,
        );
    },
    [onBeforeCompileCenter, logoRgb],
  );

  if (backCount === 0) return null;
  return (
    <instancedMesh
      ref={meshRef}
      args={[plane, undefined, backCount]}
      renderOrder={40}
    >
      <meshBasicMaterial
        key={`logo-back-${logoRgb ? "rgb" : "solid"}`}
        map={displayTexture}
        color="#ffffff"
        transparent
        alphaTest={CENTER_ALPHA_TEST}
        toneMapped={false}
        depthWrite={false}
        side={FrontSide}
        defines={logoRgb ? { USE_RGB_WHEEL_CENTER: 1 } : {}}
        onBeforeCompile={compileBack}
      />
    </instancedMesh>
  );
}

class SceneErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; message: string }
> {
  state = { hasError: false, message: "" };

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      message: error?.message || "3D assets failed to load.",
    };
  }

  override render() {
    if (this.state.hasError) {
      return (
        <Html center>
          <div className="scene-error">
            <strong>Could not load the 3D model.</strong>
            <span>Please reload the page and try again.</span>
            {this.state.message ? <span>{this.state.message}</span> : null}
          </div>
        </Html>
      );
    }
    return this.props.children;
  }
}

/* ─── Shared scene context ─────────────────────────────────────────────────
 * Holds aperture-derived geometry and shader plumbing that MirrorGroup
 * computes once per (shape, size, depthLayers) change. Each Section
 * subscribes to its own state slices via `useConfigurator(...)` and pulls
 * these stable refs from context — so a centerText keystroke only
 * re-renders <CenterTextSection>, not <BarContourSection> or anyone else.
 *
 * Previously every state edit re-rendered MirrorGroup's ~900 lines of JSX
 * (App passed `state` down as a prop). With App passive + sections
 * subscribing per-slice, MirrorGroup itself only re-renders when its own
 * subscribed slices (shape/size/depth/frame) actually change.
 */

interface SceneShared {
  // Geometry — recomputed only on shape/size/depthLayers change.
  worldHull: AperturePolygon;
  targetW: number;
  targetH: number;
  /** Hull bbox span (XY world units). */
  w: number;
  h: number;
  /** Reference text-layout span (TEXT_LAYOUT_REF_INCH). */
  textLayoutCapsWH: { w: number; h: number };
  /** Cached `getCachedPolygonShapeGeometry` per layer (deepest → frontmost). */
  layerGeometries: BufferGeometry[];
  /** Per-layer `{i, t, z, scale}` sampled for depth interpolation. */
  layerDepthSamples: Array<{ i: number; t: number; z: number; scale: number }>;
  /** Back-plane geometry (deepest cavity surface). */
  backGeo: BufferGeometry;
  /** Glass portal plane geometry. */
  glassGeo: BufferGeometry;
  /** Canonical LED point count (used by point-style layers). */
  ledPointCount: number;
  /** Total depth layers. */
  nLayers: number;
  /** Cavity geometry constants (front-most layer scale + Z bounds). */
  cavityZFront: number;
  cavityZBack: number;
  cavityScaleFront: number;

  // Shader hooks — uniforms shared with the RGB driver via reference.
  onBeforeCompileLedBasic: (s: ShaderCompileParams) => void;
  onBeforeCompileLedSolid: (s: ShaderCompileParams) => void;
  onBeforeCompileCenterText: (s: ShaderCompileParams) => void;
  onBeforeCompileCenterFull: (s: ShaderCompileParams) => void;

  /** UV bbox uniform for center text RGB wheel — synced when text canvas rebakes. */
  rgbCenterTextBoundsUniform: { value: Vector4 };
}

const SceneSharedContext = createContext<SceneShared | null>(null);
function useSceneShared(): SceneShared {
  const v = useContext(SceneSharedContext);
  if (!v) throw new Error("Section rendered outside SceneSharedContext");
  return v;
}

/**
 * Returns `value` delayed by `delayMs`. While the user is typing fast (<delay
 * between keystrokes) only the trailing value reaches consumers, so the
 * heavy `makeTextTexture` rebake is skipped on every intermediate keystroke.
 * Dropdown / swatch / toggle inputs are NOT debounced — only freeform text
 * (centerText, ledTextContent) is, since those are
 * the ones that fire dozens of patches per second during typing.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Debounce window for typed text fields — short enough that the 3D update
 * still feels live (~one 60Hz frame after the user stops typing) but long
 * enough to coalesce a normal typing burst (3-7 chars/sec) into a single
 * texture rebake.
 */
const TEXT_DEBOUNCE_MS = 110;

/**
 * Ensure a visible separator between repeated perimeter-text tiles.
 * Without this, the wrap seam can visually "tighten" on one edge/corner
 * (`...TXTM...`) depending on glyph side-bearings and where the UV seam lands.
 */
function makeWallTextTileContent(raw: string): string {
  const base = raw.trim() || DEFAULT_LED_TEXT_LIGHTING_PLACEHOLDER;
  return ` ${base} `;
}

/**
 * Bumps a generation counter once `document.fonts.load(family)` resolves.
 * Used by text-bakery `useMemo`s to retry `fillText` after the requested font
 * actually arrives (the default-bake before resolution falls back to the next
 * family in the stack, which usually shifts metrics).
 */
/**
 * Loaded-font registry. `ensureFontReady` already de-dupes the same-family
 * promise, but it always returns a `Promise` (even when the family is
 * already decoded), and `then(setGen)` always schedules a microtask which
 * lands in a *separate* React commit — so even a cached "Open Sans" caused
 * a redundant wall-text re-bake right after the initial commit. Tracking
 * which families have actually finished loading lets us skip the gen bump
 * when the font is already on hand: no second commit, no second texImage2D
 * upload, no second shader-state thrash.
 *
 * The first time we see a family we still wait on `ensureFontReady` and
 * bump `gen` once it resolves, so the FOUT-recovery semantics from before
 * are preserved.
 *
 * NOTE: an earlier version pre-warmed "Open Sans" via a top-level
 * `void ensureFontReady("Open Sans")` call. That fired a Google Fonts
 * stylesheet fetch *before* CPB had a chance to bootstrap, which (likely
 * because it competed with CPB's own bundle / asset fetches) measured ~2 s
 * extra blocking on each subsequent `*→text` click and ~70 % longer CPB
 * loader time end-to-end. Reverted — the registry is now populated lazily
 * by `useFontBakeGeneration`, which only triggers `ensureFontReady` once a
 * specific family is actually requested by `ledTextFont` / `font` slices.
 */
const loadedFontFamilies = new Set<string>();

function useFontBakeGeneration(family: string): number {
  const [gen, setGen] = useState(0);
  useEffect(() => {
    if (!family) return;
    if (loadedFontFamilies.has(family)) return;
    let cancelled = false;
    void ensureFontReady(family).then(() => {
      if (cancelled) return;
      loadedFontFamilies.add(family);
      setGen((g) => g + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [family]);
  return gen;
}

/**
 * Wall-text + LED rails section. Subscribes only to text/lighting slices; on
 * shape/size/depth changes it picks up the latest geometry from
 * SceneSharedContext. Re-renders only when the perimeter‑text inputs change.
 */
function WallTextSection() {
  const shared = useSceneShared();
  const {
    worldHull,
    w,
    h,
    nLayers,
    layerDepthSamples,
    cavityZFront,
    cavityZBack,
    cavityScaleFront,
    onBeforeCompileLedBasic,
    onBeforeCompileLedSolid,
  } = shared;

  const lightStyle = useConfigurator((s) => s.lightStyle);
  const ledTextContent = useConfigurator((s) => s.ledTextContent);
  const ledTextColor = useConfigurator((s) => s.ledTextColor);
  const ledTextRgb = useConfigurator((s) => s.ledTextRgb);
  const ledTextFont = useConfigurator((s) => s.ledTextFont);
  const ledRailsEnabled = useConfigurator((s) => s.ledRailsEnabled);
  const ledRailsColor = useConfigurator((s) => s.ledRailsColor);
  const ledRailsRgb = useConfigurator((s) => s.ledRailsRgb);
  const ledRailsMode = useConfigurator((s) => s.ledRailsMode);
  const depthLayers = useConfigurator((s) => s.depthLayers);
  const brightness = useConfigurator((s) => s.brightness);

  // Debounce only the typed text content. Two textures + N geometries get
  // rebuilt per channel on every change — coalescing keystrokes here is the
  // single biggest win for the "Text Lighting" flow.
  const debouncedLedText = useDebouncedValue(ledTextContent, TEXT_DEBOUNCE_MS);

  const fontBakeGeneration = useFontBakeGeneration(ledTextFont);

  /**
   * Wall-text channel description — note this is **independent of
   * `lightStyle`**. The text texture + merged-band geometries are baked
   * once at app load and persist across `lightStyle` toggles; entering
   * Text Lighting only flips `visible` on the already-mounted meshes
   * instead of rebuilding everything from scratch.
   *
   * Why: previously the channel was gated on `lightStyle === "text"`, so
   * every `*→text` transition disposed and re-baked the full pipeline
   * (canvas bake, texImage2D upload to GPU, fresh material instances → first
   * MSL/Metal pipeline-state object on macOS). That paid ~90–150 ms of
   * first-painted-frame cost on every entry — what the user perceived as a
   * "text mode freeze". Keeping the channel persistent moves that cost to
   * one-time-at-startup work the user never sees as a hitch.
   *
   * Side effect: text bake also runs in non-text modes when the user edits
   * the typed text / font / color. That's ~5 ms of work spread across a
   * debounced edit stream — invisible.
   */
  const wallTextChannels = useMemo<WallTextChannelSource[]>(() => {
    const fontSize = TEXT_FONT_SIZE_REF;
    const content = makeWallTextTileContent(debouncedLedText);
    return [
      {
        id: "unified",
        content,
        color: ledTextColor,
        rgb: ledTextRgb,
        font: ledTextFont,
        fontSize,
        arcs: null,
      },
    ];
  }, [
    debouncedLedText,
    ledTextColor,
    ledTextRgb,
    ledTextFont,
  ]);

  /**
   * Wall-text renders are built synchronously in `useMemo` — the result is
   * available on the same render that triggered it, so Text Lighting appears
   * instantly the moment `lightStyle` flips to `"text"`.
   *
   * History: an earlier version chunked work across `requestAnimationFrame`
   * ticks because per-channel cost was guessed at ~10–20 ms. Real numbers
   * came in at ~1–3 ms (small canvas bake + cached geometry lookups), so
   * the rAF chunker was paying ~80 ms of dead time per chunk to protect
   * <2 ms of work — the user-visible result was a slow Text-Lighting
   * appearance. A second iteration moved the work into `useEffect` (still
   * synchronous, no chunking), but `useEffect` runs **after** React's paint
   * commits, so the texture appeared one frame late at ~85 ms in this
   * scene's heavy bloom pipeline. Going back to `useMemo` lands the bake
   * inside the same render phase: a few ms added to a ~85 ms frame is
   * imperceptible, and the texture is up the moment the click commits.
   */
  const wallTextRenders = useMemo<WallTextChannelRender[]>(() => {
    if (wallTextChannels.length === 0 || layerDepthSamples.length === 0) {
      return [];
    }
    const built: WallTextChannelRender[] = [];

    const bandCount = layerDepthSamples.length;
    const maxLineWidthPx = 8000;
    const cavityDepth = Math.max(0.01, cavityZFront - cavityZBack);
    const depthPerBand = cavityDepth / Math.max(1, depthLayers);
    const ledRailsOn = ledRailsEnabled;
    const isDoubleRails = ledRailsMode === "double";
    const railSideInsetFrac = ledRailsOn
      ? LED_RAIL_INSET_FRAC + TEXT_RAIL_CLEARANCE_FRAC
      : 0;
    const wallSpanFrac = ledRailsOn
      ? isDoubleRails
        ? 1 - 2 * railSideInsetFrac
        : 1 - railSideInsetFrac
      : 1;
    const wallDepth = depthPerBand * wallSpanFrac;

    for (const src of wallTextChannels) {
      const fill = src.rgb ? "#ffffff" : src.color;
      const textMap = makeTextTexture(src.content, fill, src.font, {
        fontSizePx: src.fontSize,
        maxLineWidthPx,
        singleLine: true,
        flipX: true,
      });
      if (!textMap) continue;
      textMap.wrapS = RepeatWrapping;
      textMap.wrapT = RepeatWrapping;

      const textMapMirrored = makeTextTexture(src.content, fill, src.font, {
        fontSizePx: src.fontSize,
        maxLineWidthPx,
        singleLine: true,
        flipX: true,
        flipY: true,
      });
      if (!textMapMirrored) {
        textMap.dispose();
        continue;
      }
      textMapMirrored.wrapS = RepeatWrapping;
      textMapMirrored.wrapT = RepeatWrapping;

      const img = textMap.image as { width: number; height: number } | undefined;
      const aspect = img && img.height > 0 ? img.width / img.height : 1;
      const fontScale = src.fontSize / TEXT_FONT_SIZE_REF;
      const targetRowHeight = Math.max(
        0.015,
        Math.min(wallDepth, Math.min(w, h) * 0.12 * fontScale),
      );
      const tileWorldWidth = Math.max(0.001, targetRowHeight * aspect);

      const totalArcLen = polygonPerimeter(worldHull, cavityScaleFront);
      const uRepeatsRaw = Math.max(1, totalArcLen / tileWorldWidth);
      /**
       * Closed-perimeter seam fix: if `repeat.x` is fractional, UV at ring
       * end (`u=1`) samples a different phase than ring start (`u=0`) and
       * the closure point can cut a glyph. Keep repeats integral so the
       * ring closes exactly on tile boundaries.
       */
      const uRepeats = Math.max(1, Math.round(uRepeatsRaw));
      textMap.repeat.set(uRepeats, 1);
      textMapMirrored.repeat.set(uRepeats, 1);
      textMap.needsUpdate = true;
      textMapMirrored.needsUpdate = true;

      const nonMirroredBands: Array<{ wall: BufferGeometry; bandIdx: number }> = [];
      const mirroredBands: Array<{ wall: BufferGeometry; bandIdx: number }> = [];
      for (let idx = 0; idx < bandCount; idx++) {
        const tBack = idx / bandCount;
        const tFront = (idx + 1) / bandCount;
        const spanT = 1 / bandCount;
        const layerNumber = bandCount - idx;
        const mirrored = layerNumber % 2 === 0;
        const oddLayer = layerNumber % 2 === 1;

        let insetBackT = 0;
        let insetFrontT = 0;
        if (ledRailsOn) {
          if (isDoubleRails) {
            insetBackT = railSideInsetFrac * spanT;
            insetFrontT = railSideInsetFrac * spanT;
          } else if (oddLayer) {
            insetFrontT = railSideInsetFrac * spanT;
          } else {
            insetBackT = railSideInsetFrac * spanT;
          }
        }

        const tBackInset = tBack + insetBackT;
        const tFrontInset = tFront - insetFrontT;
        const z0 = layerZPos(tBackInset);
        const z1 = layerZPos(tFrontInset);
        const scaleBack = layerScale(tBackInset);
        const scaleFront = layerScale(tFrontInset);

        const wall = getCachedPerimeterWallGeometry(
          worldHull,
          src.arcs,
          scaleFront,
          scaleBack,
          z1,
          z0,
        );
        (mirrored ? mirroredBands : nonMirroredBands).push({
          wall,
          bandIdx: idx,
        });
      }

      // Two merged geometries per channel (one per UV-mirroring) replace what
      // used to be N=11 separate <mesh>-es. Each vertex carries its source
      // band's index in `aBandIdx`; the shader patch (`injectBandAlpha`) picks
      // the band's faded opacity from a uniform array, so per-band alpha is
      // preserved without per-band materials / meshes / draw calls.
      const geoNonMirrored = mergeBandWallGeometries(nonMirroredBands);
      const geoMirrored = mergeBandWallGeometries(mirroredBands);

      built.push({
        id: src.id,
        source: src,
        textMap,
        textMapMirrored,
        uRepeats,
        bandCount,
        geoNonMirrored,
        geoMirrored,
      });
    }

    return built;
  }, [
    wallTextChannels,
    layerDepthSamples,
    worldHull,
    cavityScaleFront,
    cavityZFront,
    cavityZBack,
    w,
    h,
    depthLayers,
    ledRailsEnabled,
    ledRailsMode,
    fontBakeGeneration,
  ]);

  // Dispose textures + merged band geometries when the memo replaces the
  // array. Cleanup runs *after* React has committed the new render with new
  // textures and geometries bound to the meshes, so freeing the previous
  // ones is safe. Per-band geometries themselves are owned by the cache —
  // we only own the *merged* result here, which is fresh per memo run.
  useEffect(() => {
    return () => {
      for (const r of wallTextRenders) {
        r.textMap.dispose();
        r.textMapMirrored.dispose();
        r.geoNonMirrored?.dispose();
        r.geoMirrored?.dispose();
      }
    };
  }, [wallTextRenders]);

  /**
   * Per-band opacity uniform array (sized for `MAX_BAND_LAYERS` = 32). The
   * vertex shader picks `uBandOpacities[ aBandIdx ]` and forwards it as
   * `vBandAlpha`, which the fragment shader multiplies into the texture's
   * alpha. Mutating the array in `useLayoutEffect` is enough — shader picks
   * up new values on the next render with no geometry / material rebuild.
   *
   * This is what lets `state.brightness` slider drag stay cheap: no rebuild
   * of the merged band geometry, no React reconcile, just a 32-element
   * Float32Array overwrite.
   */
  const bandOpacitiesUniform = useMemo(
    () => ({ value: new Float32Array(32) }),
    [],
  );
  useLayoutEffect(() => {
    const arr = bandOpacitiesUniform.value;
    for (let i = 0; i < arr.length; i++) {
      arr[i] = i < nLayers ? layerOpacityAt(i, nLayers, brightness) : 0;
    }
  }, [bandOpacitiesUniform, nLayers, brightness]);

  // Wrapped onBeforeCompile callbacks: same as the section-shared ones
  // (`onBeforeCompileLedSolid` / `…Basic`) but also inject `aBandIdx` →
  // `vBandAlpha` and the `uBandOpacities` uniform. Used **only** by the
  // merged-band wall-text meshes — rails keep using the unwrapped variants
  // because they're per-band <mesh>-es with their own `material.opacity`.
  const onBeforeCompileLedSolidBands = useCallback(
    (shader: ShaderCompileParams) => {
      onBeforeCompileLedSolid(shader);
      injectBandAlpha(shader, bandOpacitiesUniform);
    },
    [onBeforeCompileLedSolid, bandOpacitiesUniform],
  );
  const onBeforeCompileLedBasicBands = useCallback(
    (shader: ShaderCompileParams) => {
      onBeforeCompileLedBasic(shader);
      injectBandAlpha(shader, bandOpacitiesUniform);
    },
    [onBeforeCompileLedBasic, bandOpacitiesUniform],
  );

  const showTextRails = lightStyle === "text" && ledRailsEnabled;
  const wallTextRails = useMemo(() => {
    if (!showTextRails || wallTextChannels.length === 0 || layerDepthSamples.length === 0) {
      return [];
    }
    const bandCount = layerDepthSamples.length;
    const isDouble = ledRailsMode === "double";
    const cavityDepth = Math.abs(layerZPos(1) - layerZPos(0));
    const dz = cavityDepth / Math.max(1, bandCount);

    const rails: {
      id: string;
      rail: BufferGeometry;
      opacityIndex: number;
      opacityDivisor: number;
      renderOrderBias: number;
    }[] = [];

    if (isDouble) {
      const spanT = 1 / Math.max(1, bandCount);
      const insetT = spanT * LED_RAIL_INSET_FRAC;
      const minGapZ = 2 * LED_RAIL_INSET_FRAC * dz;
      const radius = Math.max(
        0.00085,
        Math.min(RAIL_RADIUS_MAX, dz * RAIL_THICKNESS_FRACTION, minGapZ * 0.42),
      );

      for (let idx = 0; idx < bandCount; idx++) {
        const tBack = idx / bandCount;
        const tFront = (idx + 1) / bandCount;
        const tDeep = tBack + insetT;
        const tShallow = tFront - insetT;
        rails.push({
          id: `rail-d-${idx}`,
          opacityIndex: idx,
          opacityDivisor: bandCount,
          renderOrderBias: 0,
          rail: getCachedPerimeterRailGeometry(
            worldHull,
            layerScale(tDeep),
            layerZPos(tDeep),
            radius,
          ),
        });
        rails.push({
          id: `rail-s-${idx}`,
          opacityIndex: idx,
          opacityDivisor: bandCount,
          renderOrderBias: 1,
          rail: getCachedPerimeterRailGeometry(
            worldHull,
            layerScale(tShallow),
            layerZPos(tShallow),
            radius,
          ),
        });
      }
    } else {
      const spanT = 1 / Math.max(1, bandCount);
      const insetT = spanT * LED_RAIL_INSET_FRAC;
      const minToBandEdgeZ = insetT * cavityDepth;
      const radius = Math.max(
        0.00085,
        Math.min(RAIL_RADIUS_MAX, dz * RAIL_THICKNESS_FRACTION, minToBandEdgeZ * 0.45),
      );

      for (let idx = 0; idx < bandCount; idx++) {
        const layerNumber = bandCount - idx;
        const tBack = idx / bandCount;
        const tFront = (idx + 1) / bandCount;
        const oddLayer = layerNumber % 2 === 1;
        const tRail = oddLayer ? tFront - insetT : tBack + insetT;
        rails.push({
          id: `rail-s-L${layerNumber}-${idx}`,
          opacityIndex: idx,
          opacityDivisor: bandCount,
          renderOrderBias: 0,
          rail: getCachedPerimeterRailGeometry(
            worldHull,
            layerScale(tRail),
            layerZPos(tRail),
            radius,
          ),
        });
      }
    }

    return rails;
  }, [
    showTextRails,
    ledRailsMode,
    wallTextChannels.length,
    layerDepthSamples,
    worldHull,
  ]);

  // Rails come from the geometry cache — no per-memo dispose needed.

  /** Stale R3F prop diff can land before the render commits when a channel
   *  switches between solid/RGB modes — repaint to keep the current solid
   *  color winning on the next frame. */
  const groupRef = useRef<Group | null>(null);
  useLayoutEffect(() => {
    if (wallTextRenders.length === 0) return;
    const colorByChannel = new Map<string, Color>();
    for (const r of wallTextRenders) {
      if (r.source.rgb) continue;
      colorByChannel.set(r.id, new Color(r.source.color));
    }
    if (colorByChannel.size === 0) return;
    groupRef.current?.traverse((obj) => {
      if (!(obj instanceof Mesh) || !(obj.material instanceof MeshBasicMaterial)) return;
      if (obj.name !== "wall-text-band") return;
      const channelId = (obj.userData as { channelId?: string }).channelId;
      if (!channelId) return;
      const col = colorByChannel.get(channelId);
      if (col) obj.material.color.copy(col);
    });
  }, [wallTextRenders]);

  // `wallTextRenders` is empty only when the bake produced no usable texture
  // (e.g. fully blank/whitespace `ledTextContent`). In every other case the
  // text meshes stay mounted regardless of `lightStyle`, with the actual
  // visibility driven by the `<group visible={…}>` toggle below — this is
  // what lets `*→text` transitions skip texture upload + first-pipeline-
  // state cost and paint the frame in <30 ms.
  const textActive = lightStyle === "text";
  if (wallTextRenders.length === 0 && wallTextRails.length === 0) return null;
  return (
    <group ref={groupRef}>
      {/*
        Wall-text meshes stay mounted across `lightStyle` toggles (texture +
        merged geometry built once at app load via `wallTextChannels`'s
        independence from `lightStyle`), but their `<group>` flips
        `visible` so we don't pay the per-frame draw cost when text isn't
        the active mode. The first `*→text` paint still depends on the
        host CPB app finishing whatever JS work it queues on the click
        (typically 100–400 ms of swatch UI / price recompute / validation),
        but the viewer-side React commit and R3F render are both <16 ms.
      */}
      <group visible={textActive}>
        {wallTextRenders.map((render) => (
          <group key={`text-channel-${render.id}`}>
            {render.geoNonMirrored && (
              <mesh
                name="wall-text-band"
                userData={{ channelId: render.id, mirrored: false }}
                geometry={render.geoNonMirrored}
                renderOrder={40}
              >
                <meshBasicMaterial
                  key={`txt-wall-${render.id}-fwd-${render.source.rgb ? "rgb" : "solid"}`}
                  map={render.textMap}
                  color={render.source.rgb ? "#ffffff" : render.source.color}
                  transparent
                  opacity={1}
                  alphaTest={CENTER_ALPHA_TEST}
                  depthWrite={false}
                  side={DoubleSide}
                  toneMapped={false}
                  {...(render.source.rgb
                    ? {
                      defines: {
                        USE_RGB_WHEEL_LED_BASIC: 1,
                        USE_RGB_WHEEL_PERIM_XY: 1,
                      },
                      onBeforeCompile: onBeforeCompileLedBasicBands,
                    }
                    : {
                      defines: { USE_LED_GAIN_SOLID: 1 },
                      onBeforeCompile: onBeforeCompileLedSolidBands,
                    })}
                />
              </mesh>
            )}
            {render.geoMirrored && (
              <mesh
                name="wall-text-band"
                userData={{ channelId: render.id, mirrored: true }}
                geometry={render.geoMirrored}
                renderOrder={41}
              >
                <meshBasicMaterial
                  key={`txt-wall-${render.id}-mir-${render.source.rgb ? "rgb" : "solid"}`}
                  map={render.textMapMirrored}
                  color={render.source.rgb ? "#ffffff" : render.source.color}
                  transparent
                  opacity={1}
                  alphaTest={CENTER_ALPHA_TEST}
                  depthWrite={false}
                  side={DoubleSide}
                  toneMapped={false}
                  {...(render.source.rgb
                    ? {
                      defines: {
                        USE_RGB_WHEEL_LED_BASIC: 1,
                        USE_RGB_WHEEL_PERIM_XY: 1,
                      },
                      onBeforeCompile: onBeforeCompileLedBasicBands,
                    }
                    : {
                      defines: { USE_LED_GAIN_SOLID: 1 },
                      onBeforeCompile: onBeforeCompileLedSolidBands,
                    })}
                />
              </mesh>
            )}
          </group>
        ))}
      </group>
      {wallTextRails.map((rail) => (
        <mesh
          key={`text-rail-${rail.id}`}
          name="wall-rail"
          geometry={rail.rail}
          renderOrder={41 + rail.opacityIndex * 3 + rail.renderOrderBias}
        >
          <meshBasicMaterial
            key={`rail-${rail.id}-${ledRailsRgb ? "rgb" : "solid"}-${ledRailsColor}`}
            color={ledRailsRgb ? "#ffffff" : ledRailsColor}
            transparent
            opacity={layerOpacityAt(rail.opacityIndex, rail.opacityDivisor, brightness)}
            depthWrite={false}
            toneMapped={false}
            {...(ledRailsRgb
              ? {
                defines: {
                  USE_RGB_WHEEL_LED_BASIC: 1,
                  USE_RGB_WHEEL_PERIM_XY: 1,
                },
                onBeforeCompile: onBeforeCompileLedBasic,
              }
              : {
                defines: { USE_LED_GAIN_SOLID: 1 },
                onBeforeCompile: onBeforeCompileLedSolid,
              })}
          />
        </mesh>
      ))}
    </group>
  );
}

/** Upper cap on tube radius (m); also limited by gap between paired rails. */
const RAIL_RADIUS_MAX = 0.0035;
/** Soft target relative to one band thickness `dz` (actual radius is min with gap). */
const RAIL_THICKNESS_FRACTION = 0.14;

/**
 * Center-text section. Subscribes to text/font/contrast/brightness slices
 * directly; geometry inputs come from SceneSharedContext so a centerText
 * keystroke doesn't require MirrorGroup to re-evaluate its memos.
 *
 * Front layer renders as a regular mesh (unique alphaTest + depthWrite);
 * back N-1 layers go through one InstancedMesh sharing a 1×1 plane —
 * per-instance matrix encodes (Z translate, pw·xy, ph·xy) and per-instance
 * opacity rides through an InstancedBufferAttribute the patched fragment
 * shader multiplies into the texture's alpha.
 */
function CenterTextSection() {
  const shared = useSceneShared();
  const { worldHull, w, h, textLayoutCapsWH, nLayers, onBeforeCompileCenterText, rgbCenterTextBoundsUniform } =
    shared;
  void worldHull;

  const designMode = useConfigurator((s) => s.designMode);
  const centerText = useConfigurator((s) => s.centerText);
  const textColor = useConfigurator((s) => s.textColor);
  const textRgb = useConfigurator((s) => s.textRgb);
  const font = useConfigurator((s) => s.font);
  const textFontSize = useConfigurator((s) => s.textFontSize);
  const centerContrast = useConfigurator((s) => s.centerContrast);
  const brightness = useConfigurator((s) => s.brightness);
  const depthLayers = useConfigurator((s) => s.depthLayers);

  // Only the freeform text content is debounced — colour swatches, font
  // dropdown and the contrast picker still react instantly. Texture rebake
  // is the expensive step (~5–15 ms per keystroke for long strings); this
  // coalesces typing bursts into one rebake at the end.
  const debouncedCenterText = useDebouncedValue(centerText, TEXT_DEBOUNCE_MS);

  const fontBakeGeneration = useFontBakeGeneration(font);

  const resolvedCenterText = useMemo(() => {
    if (designMode !== "text") return "";
    const t = sanitizeCenterTextInput(debouncedCenterText);
    return t.length > 0 ? t : DEFAULT_CENTER_DISPLAY_TEXT;
  }, [designMode, debouncedCenterText]);

  const textFrontPlaneAspect = useMemo(() => {
    const n = depthLayers;
    if (n < 1) return 1;
    const sReq = clampCenterTextPreviewScale();
    const t = layerProgress(n - 1, n);
    const sc = layerScale(t);
    const holeW = w * sc * CENTER_DESIGN_FILL;
    const holeH = h * sc * CENTER_DESIGN_FILL;
    const fontScale = textFontSize / TEXT_FONT_SIZE_REF;
    const desiredW = textLayoutCapsWH.w * sc * CENTER_DESIGN_FILL * fontScale;
    const desiredH = textLayoutCapsWH.h * sc * CENTER_DESIGN_FILL * fontScale;
    const pw = Math.min(holeW / sReq, desiredW);
    const ph = Math.min(holeH / sReq, desiredH);
    return Math.max(1e-4, pw / ph);
  }, [depthLayers, textFontSize, w, h, textLayoutCapsWH]);

  /**
   * Center-text canvas bake runs synchronously in `useMemo`. Empirically the
   * bake takes ~1–2 ms even for long strings — well under any threshold
   * worth deferring. An earlier rAF defer added a full frame of dead time
   * (~80 ms in this scene's heavy bloom pipeline) before the texture
   * appeared, which is what made `designMode: logo → text` feel slow.
   * Debounce on `centerText` (in the parent `useDebouncedValue` call) keeps
   * the bake from firing on every keystroke.
   */
  const centerTextMap = useMemo<CanvasTexture | null>(() => {
    if (designMode !== "text" || !resolvedCenterText) return null;
    const fill = textRgb ? "#ffffff" : textColor;
    const maxLineWidthPx = Math.min(
      4000,
      Math.max(64, Math.round(w * TEXT_WRAP_PX_PER_METER * CENTER_DESIGN_FILL)),
    );
    const outline =
      centerContrast === "none"
        ? undefined
        : {
          colorHex: centerContrast === "dark" ? "#000000" : "#ffffff",
          widthPx: 2,
        };
    return makeTextTexture(resolvedCenterText, fill, font, {
      fontSizePx: textFontSize,
      maxLineWidthPx,
      texturePlaneAspect: textFrontPlaneAspect,
      outline,
    });
  }, [
    designMode,
    resolvedCenterText,
    textColor,
    textRgb,
    font,
    textFontSize,
    centerContrast,
    w,
    textFrontPlaneAspect,
    fontBakeGeneration,
  ]);

  useEffect(() => () => centerTextMap?.dispose(), [centerTextMap]);

  // Sync text-bbox uniform whenever the rendered text canvas is rebuilt. The
  // uniform reference itself is stable; only its `.value` changes, so already-
  // compiled materials keep working without recompile.
  useLayoutEffect(() => {
    const ub = centerTextMap?.userData?.uvBounds as
      | [number, number, number, number]
      | undefined;
    if (ub && ub.length === 4) {
      rgbCenterTextBoundsUniform.value.set(ub[0], ub[1], ub[2], ub[3]);
    } else {
      rgbCenterTextBoundsUniform.value.set(0, 0, 1, 1);
    }
  }, [centerTextMap, rgbCenterTextBoundsUniform]);

  const centerTextLayerPlanes = useMemo(() => {
    if (!centerTextMap) return [] as { pw: number; ph: number; xyScale: number }[];
    const sReq = clampCenterTextPreviewScale();
    const ledMargin = 0.988;
    const n = depthLayers;
    return Array.from({ length: n }, (_, i) => {
      const t = layerProgress(i, n);
      const sc = layerScale(t);
      const holeW = w * sc * CENTER_DESIGN_FILL;
      const holeH = h * sc * CENTER_DESIGN_FILL;
      const fontScale = textFontSize / TEXT_FONT_SIZE_REF;
      const desiredW = textLayoutCapsWH.w * sc * CENTER_DESIGN_FILL * fontScale;
      const desiredH = textLayoutCapsWH.h * sc * CENTER_DESIGN_FILL * fontScale;
      const pw = Math.min(holeW / sReq, desiredW);
      const ph = Math.min(holeH / sReq, desiredH);
      const capW = Math.max(1, (ledMargin * holeW) / Math.max(pw, 1e-6));
      const capH = Math.max(1, (ledMargin * holeH) / Math.max(ph, 1e-6));
      const xyScale = Math.min(sReq, capW, capH);
      return { pw, ph, xyScale };
    });
  }, [centerTextMap, depthLayers, textFontSize, w, h, textLayoutCapsWH]);

  // UNIT_PLANE is shared module-wide; no per-mount allocation / dispose.
  const sharedPlane = UNIT_PLANE;

  const instancedRef = useRef<InstancedMesh | null>(null);
  const opacityAttrRef = useRef<InstancedBufferAttribute | null>(null);
  const backLayerCount = Math.max(0, centerTextLayerPlanes.length - 1);

  useEffect(() => {
    const mesh = instancedRef.current;
    if (!mesh || backLayerCount === 0) return;
    /**
     * Reuse the same Float32Array + InstancedBufferAttribute across renders.
     * Previously we allocated a fresh attribute on every brightness tick
     * (~10/sec while dragging the slider) which forced a full GPU re-upload
     * even though the contents barely changed. Reallocate only when
     * `backLayerCount` (i.e. depthLayers) changes.
     */
    let attr = opacityAttrRef.current;
    if (!attr || attr.array.length !== backLayerCount) {
      attr = new InstancedBufferAttribute(new Float32Array(backLayerCount), 1);
      opacityAttrRef.current = attr;
      mesh.geometry.setAttribute("instanceOpacity", attr);
    } else if (mesh.geometry.getAttribute("instanceOpacity") !== attr) {
      mesh.geometry.setAttribute("instanceOpacity", attr);
    }

    const m = new Matrix4();
    const pos = new Vector3();
    const q = new Quaternion();
    const sc = new Vector3();
    const arr = attr.array as Float32Array;
    for (let i = 0; i < backLayerCount; i++) {
      const layer = centerTextLayerPlanes[i];
      const t = layerProgress(i, nLayers);
      const zPos = layerZPos(t) + LAYER_Z_EPS;
      pos.set(0, 0, zPos);
      sc.set(layer.pw * layer.xyScale, layer.ph * layer.xyScale, 1);
      m.compose(pos, q, sc);
      mesh.setMatrixAt(i, m);
      arr[i] = layerOpacityAt(i, nLayers, brightness);
    }
    mesh.instanceMatrix.needsUpdate = true;
    attr.needsUpdate = true;
    mesh.count = backLayerCount;
  }, [centerTextLayerPlanes, nLayers, brightness, backLayerCount, sharedPlane]);

  const compileBack = useCallback(
    (shader: ShaderCompileParams) => {
      if (textRgb) onBeforeCompileCenterText(shader);
      shader.vertexShader = shader.vertexShader
        .replace(
          "#include <common>",
          `#include <common>
           attribute float instanceOpacity;
           varying float vInstanceOpacity;`,
        )
        .replace(
          "#include <begin_vertex>",
          `#include <begin_vertex>
           vInstanceOpacity = instanceOpacity;`,
        );
      shader.fragmentShader = shader.fragmentShader
        .replace(
          "#include <common>",
          `#include <common>
           varying float vInstanceOpacity;`,
        )
        // three.js renamed `output_fragment` → `opaque_fragment` around r150;
        // we're on 0.170. Multiply alpha AFTER the chunk so our value isn't
        // overwritten by the chunk's `gl_FragColor = vec4(outgoingLight,
        // diffuseColor.a)` assignment.
        .replace(
          "#include <opaque_fragment>",
          `#include <opaque_fragment>
           gl_FragColor.a *= vInstanceOpacity;`,
        );
    },
    [onBeforeCompileCenterText, textRgb],
  );

  // Repaint white when textRgb is off (handles a stale R3F prop diff during
  // solid → RGB toggle).
  const groupRef = useRef<Group | null>(null);
  useLayoutEffect(() => {
    if (!centerTextMap || designMode !== "text" || textRgb) return;
    const white = new Color("#ffffff");
    groupRef.current?.traverse((obj) => {
      if (obj instanceof Mesh && obj.material instanceof MeshBasicMaterial) {
        obj.material.color.copy(white);
      }
    });
  }, [centerTextMap, designMode, textRgb, centerTextLayerPlanes]);

  if (designMode !== "text" || !centerTextMap || centerTextLayerPlanes.length === 0)
    return null;

  const front = centerTextLayerPlanes[centerTextLayerPlanes.length - 1];
  const frontIdx = centerTextLayerPlanes.length - 1;
  const tFront = layerProgress(frontIdx, nLayers);
  const zFront = layerZPos(tFront) + LAYER_Z_EPS;
  const frontOpacity = layerOpacityAt(frontIdx, nLayers, brightness);

  return (
    <group ref={groupRef}>
      <mesh
        geometry={sharedPlane}
        scale={[front.pw * front.xyScale, front.ph * front.xyScale, 1]}
        position={[0, 0, zFront]}
        renderOrder={40 + frontIdx}
      >
        <meshBasicMaterial
          key={`txt-front-${textRgb ? "rgb" : "solid"}`}
          map={centerTextMap}
          color="#ffffff"
          transparent
          opacity={frontOpacity}
          alphaTest={FRONT_LAYER_ALPHA_TEST}
          depthWrite
          side={FrontSide}
          toneMapped={false}
          {...(textRgb
            ? {
              defines: { USE_RGB_WHEEL_CENTER: 1 },
              onBeforeCompile: onBeforeCompileCenterText,
            }
            : {})}
        />
      </mesh>

      {backLayerCount > 0 && (
        <instancedMesh
          ref={instancedRef}
          args={[sharedPlane, undefined, backLayerCount]}
          renderOrder={40}
        >
          <meshBasicMaterial
            key={`txt-back-${textRgb ? "rgb" : "solid"}`}
            map={centerTextMap}
            color="#ffffff"
            transparent
            alphaTest={CENTER_ALPHA_TEST}
            depthWrite={false}
            side={FrontSide}
            toneMapped={false}
            defines={textRgb ? { USE_RGB_WHEEL_CENTER: 1 } : {}}
            onBeforeCompile={compileBack}
          />
        </instancedMesh>
      )}
    </group>
  );
}

/**
 * Bar-style LED contour tubes. Builds the per-layer tube geometries from the
 * shared aperture; subscribes to lightStyle / color / rgb / brightness so it
 * re-renders only on lighting edits.
 */
function BarContourSection() {
  const { worldHull, nLayers, onBeforeCompileLedBasic, onBeforeCompileLedSolid } =
    useSceneShared();
  const lightStyle = useConfigurator((s) => s.lightStyle);
  const rgb = useConfigurator((s) => s.rgb);
  const ledColor = useConfigurator((s) => s.ledColor);
  const brightness = useConfigurator((s) => s.brightness);
  const depthLayers = useConfigurator((s) => s.depthLayers);

  /**
   * Bar tubes come from `getCachedBarTubeGeometries` (LRU). After the first
   * cold visit, every per-(shape, layer) lookup is O(1). Even on cold path
   * the total cost is a few ms, so we build synchronously in `useMemo` —
   * splitting across rAF ticks turned out to add far more dead time
   * (one frame ≈ ~80 ms in this scene's bloom pipeline) than the work
   * we were trying to protect.
   */
  const barContourGeometries = useMemo<BufferGeometry[][]>(() => {
    if (lightStyle !== "bar") return [];
    return Array.from({ length: depthLayers }, (_, layerIdx) => {
      const t = layerMidProgress(layerIdx, depthLayers);
      const sc = layerScale(t);
      const z = layerZPos(t) - LED_DEPTH_PUSH;
      return getCachedBarTubeGeometries(worldHull, sc, z);
    });
  }, [lightStyle, depthLayers, worldHull]);

  if (lightStyle !== "bar" || barContourGeometries.length === 0) return null;
  return (
    <group>
      {barContourGeometries.map((layerGeoms, layerIdx) => {
        const opacity = layerOpacityAt(layerIdx, nLayers, brightness);
        return (
          <group key={`led-layer-${layerIdx}`}>
            {layerGeoms.map((geo, edgeIdx) => (
              <mesh
                key={`led-tube-${layerIdx}-${edgeIdx}`}
                geometry={geo}
                renderOrder={50 + layerIdx}
              >
                {rgb ? (
                  <meshBasicMaterial
                    key="led-rgb"
                    color="#ffffff"
                    toneMapped={false}
                    transparent
                    opacity={opacity}
                    depthWrite={false}
                    defines={{ USE_RGB_WHEEL_LED_BASIC: 1 }}
                    onBeforeCompile={onBeforeCompileLedBasic}
                  />
                ) : (
                  <meshBasicMaterial
                    key="led-solid"
                    color={ledColor}
                    toneMapped={false}
                    transparent
                    opacity={opacity}
                    depthWrite={false}
                    defines={{ USE_LED_GAIN_SOLID: 1 }}
                    onBeforeCompile={onBeforeCompileLedSolid}
                  />
                )}
              </mesh>
            ))}
          </group>
        );
      })}
    </group>
  );
}

/**
 * Point-style LEDs. Builds per-layer point samples from the shared aperture;
 * subscribes to the same lighting slices as BarContourSection.
 */
function LedPointSection() {
  const { worldHull, ledPointCount, nLayers, onBeforeCompileLedBasic, onBeforeCompileLedSolid } =
    useSceneShared();
  const lightStyle = useConfigurator((s) => s.lightStyle);
  const rgb = useConfigurator((s) => s.rgb);
  const ledColor = useConfigurator((s) => s.ledColor);
  const brightness = useConfigurator((s) => s.brightness);
  const depthLayers = useConfigurator((s) => s.depthLayers);

  /**
   * LED point samples are cached in `getCachedLedPointSamples` (LRU). Cold
   * path runs an inset solve + perimeter sampling per layer (a few ms total
   * for typical depthLayers/ledPointCount); warm path is sub-millisecond.
   * Same reasoning as BarContourSection — synchronous `useMemo` is faster
   * end-to-end than rAF chunking in this scene.
   */
  const ledPointLayers = useMemo<LedPointSample[][]>(() => {
    if (lightStyle !== "point") return [];
    return Array.from({ length: depthLayers }, (_, layerIdx) => {
      const t = layerMidProgress(layerIdx, depthLayers);
      const sc = layerScale(t);
      return getCachedLedPointSamples(worldHull, 1, sc, ledPointCount);
    });
  }, [lightStyle, depthLayers, worldHull, ledPointCount]);

  if (lightStyle !== "point") return null;
  return (
    <group>
      {ledPointLayers.map((layerPts, layerIdx) => {
        const t = layerMidProgress(layerIdx, nLayers);
        const z = layerZPos(t) - LED_DEPTH_PUSH;
        const opacity = layerOpacityAt(layerIdx, nLayers, brightness);
        return (
          <LedPointsInstancedLayer
            key={layerIdx}
            points={layerPts}
            z={z}
            color={ledColor}
            rgb={rgb}
            opacity={opacity}
            renderOrder={50 + layerIdx}
            onBeforeCompileSolid={onBeforeCompileLedSolid}
            onBeforeCompileBasic={onBeforeCompileLedBasic}
          />
        );
      })}
    </group>
  );
}

/**
 * Reflection / depth planes inside the cavity. Subscribes to brightness only;
 * geometries come from SceneSharedContext (rebuilt only on shape/size/depth).
 */
function DepthPlanesSection() {
  const { layerGeometries, nLayers } = useSceneShared();
  const brightness = useConfigurator((s) => s.brightness);
  return (
    <>
      {layerGeometries.map((geo, i) => {
        const t = layerProgress(i, nLayers);
        const zPos = layerZPos(t);
        const opacity = Math.min((1 - t) * 0.55 * brightness, 0.5);
        return (
          <mesh key={i} geometry={geo} position={[0, 0, zPos]} renderOrder={10 + i}>
            <meshBasicMaterial
              color={DEPTH_PLANE_COLOR}
              transparent
              opacity={opacity}
              depthWrite={false}
              side={FrontSide}
            />
          </mesh>
        );
      })}
    </>
  );
}

/**
 * Tiny 1×1 dummy CanvasTexture used as a stand-in `map` for warmup meshes.
 * Three.js bakes `USE_MAP` into the program key when a material has a `.map`
 * set, so warmup meshes that need the map-using shader variant must
 * actually have a (non-null) map. The pixel data is irrelevant — three.js
 * cares about the slot existence at compile time, not the content.
 */
const _warmupCanvas = (() => {
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = 1;
  c.height = 1;
  return c;
})();
const WARMUP_TEXTURE = _warmupCanvas ? new CanvasTexture(_warmupCanvas) : null;

/**
 * Pre-compile every GLSL program currently mounted in the scene.
 *
 * Three.js compiles materials lazily on their first draw. For a material
 * with custom `defines` + `onBeforeCompile` patches + bloom post-process +
 * multiple lights that's 50–200 ms inside R3F's render loop the moment a
 * material first appears (e.g. when the user clicks "Text Lighting" and a
 * fresh wall-text band material mounts). The orbit camera freezes for the
 * compile duration.
 *
 * `compileAsync` (three.js r152+) runs the compile via the
 * `KHR_parallel_shader_compile` extension when the GPU driver exposes it
 * — the work happens off the main thread, so even on cold compile the
 * camera keeps animating. We fall back to synchronous `compile` if the
 * runtime doesn't expose the async variant.
 *
 * Mounted both in the **outer** scene (frame + lights) and inside the
 * `MeshPortalMaterial` (cavity contents) — `useThree()` returns whichever
 * scene/camera context the component sits in, so one instance per scope
 * precompiles everything that scope renders.
 */
function ScenePrecompiler() {
  const { gl, scene, camera } = useThree();
  useEffect(() => {
    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      const renderer = gl as unknown as {
        compileAsync?: (s: typeof scene, c: typeof camera) => Promise<void>;
        compile: (s: typeof scene, c: typeof camera) => void;
      };
      try {
        if (typeof renderer.compileAsync === "function") {
          // Promise rejection in the rare disposal-mid-mount case is harmless;
          // the renderer cleanup will release any in-flight programs.
          void renderer.compileAsync(scene, camera).catch(() => undefined);
        } else {
          renderer.compile(scene, camera);
        }
      } catch {
        /* renderer disposed mid-mount — safe to ignore */
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [gl, scene, camera]);
  return null;
}

/**
 * Match the canonical lightStyle labels from CPB's option catalogue. Mirrors
 * `LIGHT_STYLE_BY_LABEL` in cpbSchemaRegistry.ts. Lowercase / trimmed compare.
 *
 * These three exact labels are unique in CPB's full catalogue (verified in
 * `cpbDiscovery.json`) — no other category has a "Solid Bar Glow" / "Text
 * Lighting" / "Point Light" option. So matching by label alone is safe and
 * doesn't require us to walk up to a `[data-category-id="…"]` ancestor (an
 * earlier attempt that quietly failed because CPB renders that attribute
 * deeper than the .cpb-option's parent chain reaches in some layouts).
 */
const LIGHT_STYLE_BY_SWATCH_LABEL: Record<string, LightStyle> = {
  "solid bar glow": "bar",
  "text lighting": "text",
  "point light": "point",
};

/**
 * Read the swatch's title text out of a `.cpb-option` element.
 *
 * CPB renders text-display swatches as `[label, "Example"]`. The "Example"
 * suffix comes from one of two paths:
 *
 *   1. `customproductbuilder.js` injects `<div class="cpb-option-description">
 *      Example</div>` when `category.extraClassName.includes('custom')`. We
 *      detect this by class and strip its textContent off the tail.
 *   2. CPB's own bundled React renders the description with no stable
 *      class hook (verified: lightStyle swatches show "Text LightingExample"
 *      in textContent yet `.cpb-option-description` is absent). We fall
 *      back to stripping a trailing literal "Example" — that word has been
 *      stable across CPB versions for the swatch description convention.
 *
 * Either path leaves the canonical label ("Text Lighting", "Solid Bar
 * Glow", "Point Light") for the caller to lowercase + look up.
 */
function readSwatchLabel(optionEl: Element): string {
  const desc = optionEl.querySelector(".cpb-option-description");
  let text = (optionEl.textContent ?? "").trim();
  if (desc) {
    const descText = (desc.textContent ?? "").trim();
    if (descText && text.endsWith(descText)) {
      text = text.slice(0, -descText.length).trim();
      return text;
    }
  }
  // Fallback for CPB swatches where the description isn't tagged as
  // .cpb-option-description (e.g. lightStyle category).
  if (text.endsWith("Example")) {
    text = text.slice(0, -"Example".length).trim();
  }
  return text;
}

/**
 * Capture-phase listener for Lighting-Style swatch clicks: applies the new
 * `lightStyle` to our store BEFORE CPB's bubble-phase handler starts its
 * 500–1100 ms of asynchronous UI work, so the 3D scene visually flips to
 * the new mode within the next browser frame (~10 ms) instead of waiting
 * for `cpb-change` to confirm.
 *
 * Why this is the *only* CPB-related listener now. An earlier
 * `CpbInteractionPauser` flipped `frameloop="never"` on every `.cpb-option`
 * click to give CPB a "clean" main thread. During diagnostics we saw the
 * browser fire rAF within ~8–11 ms of the click — i.e. CPB does NOT block the
 * main thread synchronously, only schedules async work. So pausing R3F
 * was costing us up to 830 ms of frozen animation per click (text input,
 * font dropdown, etc. wait that long for `cpb-change`) without speeding
 * CPB up at all. Removed entirely; only the optimistic-flip path remains.
 *
 * Defaults note. The store's text-mode defaults (`ledTextColor: #ffffff`,
 * `ledTextFont: "Open Sans"`) are matched to CPB's first-text-entry
 * payload, so the optimistic flip on the very first `*→text` click renders
 * the same visual state CPB would eventually confirm — no color/font flash
 * when `cpb-change` arrives.
 */
function LightStyleOptimisticListener() {
  /**
   * `advance` + `invalidate` — read with separate selectors so we never
   * subscribe via a freshly allocated object each frame.
   */
  const advance = useThree((s) => s.advance);
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => {
    /**
     * Same-click-cycle gate. A single user gesture fires `pointerdown`
     * (where we apply the patch) followed ~30–80 ms later by `click`
     * for the same target. The click handler must NOT try to apply
     * the patch again — `getState().lightStyle === next` is already
     * true. Reset on the next `cpb-change` (one click cycle = one
     * CPB confirmation).
     */
    let optimisticAppliedStyle: LightStyle | null = null;

    const apply = (target: Element | null) => {
      const optionEl = target?.closest?.(".cpb-option") ?? null;
      if (!optionEl) return;
      const label = readSwatchLabel(optionEl).toLowerCase();
      const next = LIGHT_STYLE_BY_SWATCH_LABEL[label] ?? null;
      if (!next) return;
      if (optimisticAppliedStyle === next) return;
      const current = getState().lightStyle;
      if (current === next) return;
      optimisticAppliedStyle = next;
      /**
       * THE critical part. Without `flushSync` the store update is
       * batched, React's commit happens on the next microtask, and the
       * next microtask runs AFTER CPB's bubble-phase click handler has
       * blocked the main thread for 400–1100 ms. The whole optimistic
       * flow then collapses into "user clicked, CPB blocks, finally
       * React commits + R3F renders the new state ~500 ms later".
       *
       * `flushSync` forces React to run the commit synchronously inside
       * our capture-phase handler — BEFORE the click bubbles to CPB.
       * The Three.js scene graph is updated immediately (toggle
       * `<group visible={textActive}>`).
       */
      flushSync(() => {
        storeSetState({ lightStyle: next });
      });
      /**
       * Now the scene graph has the new state but the GPU backbuffer
       * still shows the old state. We need to *paint* a full frame
       * synchronously before yielding to the click bubble, so the
       * compositor has a fresh image to present at the next vsync —
       * even while the main thread is then blocked for the full
       * 1100 ms by CPB's text-mode UI rebuild.
       *
       * Why `advance(time, true)` instead of `gl.render(scene, camera)`:
       * the scene runs through `<EffectComposer>` (Bloom + SMAA + Lens
       * Flare). When a composer is registered, R3F replaces the default
       * render with `composer.render()` and `gl` is left in whatever
       * render-target state the composer last set (typically pointing
       * at the composer's offscreen swap targets, NOT the canvas
       * backbuffer). Calling `gl.render(scene, camera)` directly here
       * draws into that offscreen target, so the canvas backbuffer
       * stays stale and the compositor presents the OLD frame for the
       * full 1.1 s CPB block — which is exactly the symptom we saw
       * (logs report fast `sync commit/gl.render`, but the user sees
       * "old state for ~1 s, then everything updates at once").
       *
       * `advance(performance.now(), true)` runs R3F's normal frame
       * pipeline synchronously: tween updates, every `useFrame`
       * subscriber (priority-sorted), and the proper render call
       * (composer.render() if registered, gl.render() otherwise). The
       * resulting frame ends up in the canvas backbuffer with full
       * post-processing applied, and the compositor can present it
       * at the next vsync independently of main-thread availability.
       */
      advance(performance.now(), true);
      // Belt-and-braces for any future demand-mode change.
      invalidate();
    };

    const onPointerDown = (ev: Event) => apply(ev.target as Element | null);
    const onClick = (ev: Event) => apply(ev.target as Element | null);
    const onCpbChangeReceived = () => {
      // Reset the gate so the next click cycle can apply optimistically.
      optimisticAppliedStyle = null;
    };

    document.body.addEventListener("pointerdown", onPointerDown, true);
    document.body.addEventListener("click", onClick, true);
    const offChange = onCpbChange(onCpbChangeReceived);

    return () => {
      document.body.removeEventListener("pointerdown", onPointerDown, true);
      document.body.removeEventListener("click", onClick, true);
      offChange();
    };
  }, [advance, invalidate]);
  return null;
}

/**
 * Mounts one tiny invisible mesh for every distinct material configuration
 * the scene can produce, so `ScenePrecompiler` (called above) actually links
 * every GLSL program ahead of time — not just the ones currently visible
 * for the default `lightStyle = bar` initial state.
 *
 * Each warmup material **must match the runtime material's program-relevant
 * properties exactly** (three.js encodes `USE_MAP`, `DOUBLE_SIDED`,
 * `USE_ALPHATEST`, `USE_INSTANCING` etc. into the program cache key). When
 * the user clicks Text Lighting / Point / RGB toggle, the renderer rebinds
 * an already-compiled program — no compile freeze.
 *
 * Trick: the whole group sits at `position={[1e5, 0, 0]}` so it's always
 * frustum-culled out of the actual render. `visible={true}` is what makes
 * `traverseVisible()` (used by `gl.compileAsync`) walk and compile its
 * materials. The first render after mount triggers the same program
 * lookups; from there on every subsequent material that matches one of
 * these configurations is instant.
 *
 * Coverage map (each entry maps to one runtime material site):
 *   - LED solid (FrontSide, no map)        → bar tubes, wall rails (solid)
 *   - LED solid (DoubleSide, no map)       → point discs (solid)
 *   - LED RGB basic (FrontSide, no map)    → bar tubes (RGB)
 *   - LED RGB + perim (FrontSide, no map)  → wall rails (RGB)
 *   - LED RGB + perim (DoubleSide, map,    → wall-text bands (both modes)
 *      alphaTest=CENTER_ALPHA_TEST)
 *   - LED solid (DoubleSide, map,          → wall-text bands (solid)
 *      alphaTest=CENTER_ALPHA_TEST)
 *   - LED RGB basic (DoubleSide, no map)   → point discs (RGB)
 *   - Center solid (FrontSide, map,        → center text + logo front (solid)
 *      alphaTest=FRONT_LAYER_ALPHA_TEST,
 *      depthWrite=true)
 *   - Center RGB (text bounds uniform)     → center text front (RGB)
 *   - Center RGB (full bounds uniform)     → logo front (RGB)
 *   - Instanced center solid (map,         → center text + logo back layers (solid)
 *      alphaTest=CENTER_ALPHA_TEST,
 *      instanceOpacity attribute)
 *   - Instanced center RGB (text)          → center text back (RGB)
 *   - Instanced center RGB (full)          → logo back (RGB)
 */
function ShaderWarmupGroup() {
  const {
    onBeforeCompileLedBasic,
    onBeforeCompileLedSolid,
    onBeforeCompileCenterText,
    onBeforeCompileCenterFull,
  } = useSceneShared();

  // Dummy `uBandOpacities` uniform for the wall-text band-alpha warmup
  // variants. `programCacheKey` is derived from shader source + defines +
  // material flags — uniform *bindings* don't affect it. So compiling here
  // with this dummy yields the same compiled program the real
  // `WallTextSection` materials (which hold their own uniform reference)
  // hit at draw time. Without this warmup, the first click on Text Lighting
  // pays a fresh compile (~50–100 ms) for a never-before-seen program key.
  const warmupBandOpacitiesUniform = useMemo(
    () => ({ value: new Float32Array(32) }),
    [],
  );
  const compileLedSolidBands = useCallback(
    (shader: ShaderCompileParams) => {
      onBeforeCompileLedSolid(shader);
      injectBandAlpha(shader, warmupBandOpacitiesUniform);
    },
    [onBeforeCompileLedSolid, warmupBandOpacitiesUniform],
  );
  const compileLedBasicBands = useCallback(
    (shader: ShaderCompileParams) => {
      onBeforeCompileLedBasic(shader);
      injectBandAlpha(shader, warmupBandOpacitiesUniform);
    },
    [onBeforeCompileLedBasic, warmupBandOpacitiesUniform],
  );

  // Same back-layer shader patch the real CenterTextSection / Logo back
  // mesh uses. Inlined here so the warmup mesh hits the same program key.
  const compileBackText = useCallback(
    (shader: ShaderCompileParams) => {
      onBeforeCompileCenterText(shader);
      shader.vertexShader = shader.vertexShader
        .replace(
          "#include <common>",
          `#include <common>
           attribute float instanceOpacity;
           varying float vInstanceOpacity;`,
        )
        .replace(
          "#include <begin_vertex>",
          `#include <begin_vertex>
           vInstanceOpacity = instanceOpacity;`,
        );
      shader.fragmentShader = shader.fragmentShader
        .replace(
          "#include <common>",
          `#include <common>
           varying float vInstanceOpacity;`,
        )
        .replace(
          "#include <opaque_fragment>",
          `#include <opaque_fragment>
           gl_FragColor.a *= vInstanceOpacity;`,
        );
    },
    [onBeforeCompileCenterText],
  );
  const compileBackFull = useCallback(
    (shader: ShaderCompileParams) => {
      onBeforeCompileCenterFull(shader);
      shader.vertexShader = shader.vertexShader
        .replace(
          "#include <common>",
          `#include <common>
           attribute float instanceOpacity;
           varying float vInstanceOpacity;`,
        )
        .replace(
          "#include <begin_vertex>",
          `#include <begin_vertex>
           vInstanceOpacity = instanceOpacity;`,
        );
      shader.fragmentShader = shader.fragmentShader
        .replace(
          "#include <common>",
          `#include <common>
           varying float vInstanceOpacity;`,
        )
        .replace(
          "#include <opaque_fragment>",
          `#include <opaque_fragment>
           gl_FragColor.a *= vInstanceOpacity;`,
        );
    },
    [onBeforeCompileCenterFull],
  );

  const map = WARMUP_TEXTURE;
  if (!map) return null;

  return (
    <group position={[1e5, 0, 0]}>
      {/* ── Bar tubes / wall rails — no map, FrontSide ─────────────────── */}
      <mesh geometry={UNIT_PLANE}>
        <meshBasicMaterial
          color="#ffffff"
          toneMapped={false}
          transparent
          opacity={0}
          depthWrite={false}
          defines={{ USE_LED_GAIN_SOLID: 1 }}
          onBeforeCompile={onBeforeCompileLedSolid}
        />
      </mesh>
      <mesh geometry={UNIT_PLANE}>
        <meshBasicMaterial
          color="#ffffff"
          toneMapped={false}
          transparent
          opacity={0}
          depthWrite={false}
          defines={{ USE_RGB_WHEEL_LED_BASIC: 1 }}
          onBeforeCompile={onBeforeCompileLedBasic}
        />
      </mesh>
      <mesh geometry={UNIT_PLANE}>
        <meshBasicMaterial
          color="#ffffff"
          toneMapped={false}
          transparent
          opacity={0}
          depthWrite={false}
          defines={{ USE_RGB_WHEEL_LED_BASIC: 1, USE_RGB_WHEEL_PERIM_XY: 1 }}
          onBeforeCompile={onBeforeCompileLedBasic}
        />
      </mesh>

      {/* ── Point LED discs — DoubleSide, no map ───────────────────────── */}
      <mesh geometry={UNIT_PLANE}>
        <meshBasicMaterial
          color="#ffffff"
          side={DoubleSide}
          toneMapped={false}
          transparent
          opacity={0}
          depthWrite={false}
          defines={{ USE_LED_GAIN_SOLID: 1 }}
          onBeforeCompile={onBeforeCompileLedSolid}
        />
      </mesh>
      <mesh geometry={UNIT_PLANE}>
        <meshBasicMaterial
          color="#ffffff"
          side={DoubleSide}
          toneMapped={false}
          transparent
          opacity={0}
          depthWrite={false}
          defines={{ USE_RGB_WHEEL_LED_BASIC: 1 }}
          onBeforeCompile={onBeforeCompileLedBasic}
        />
      </mesh>

      {/* ── Wall-text merged bands — DoubleSide + map + alphaTest +
          per-vertex aBandIdx → uBandOpacities band-alpha patch ──────────── */}
      <mesh geometry={UNIT_PLANE}>
        <meshBasicMaterial
          map={map}
          color="#ffffff"
          side={DoubleSide}
          toneMapped={false}
          transparent
          opacity={0}
          alphaTest={CENTER_ALPHA_TEST}
          depthWrite={false}
          defines={{ USE_LED_GAIN_SOLID: 1 }}
          onBeforeCompile={compileLedSolidBands}
        />
      </mesh>
      <mesh geometry={UNIT_PLANE}>
        <meshBasicMaterial
          map={map}
          color="#ffffff"
          side={DoubleSide}
          toneMapped={false}
          transparent
          opacity={0}
          alphaTest={CENTER_ALPHA_TEST}
          depthWrite={false}
          defines={{ USE_RGB_WHEEL_LED_BASIC: 1, USE_RGB_WHEEL_PERIM_XY: 1 }}
          onBeforeCompile={compileLedBasicBands}
        />
      </mesh>
      {/* Wall rails (LED tubes) keep using the unwrapped `LedSolid`/`LedBasic`
          variants — they're per-band <mesh>-es with their own
          `material.opacity` so they don't need the band-alpha patch. The
          bar-tubes warmup at the top of this group already covers them. */}

      {/* ── Center text + logo FRONT — map + alphaTest=0.5 + depthWrite ── */}
      <mesh geometry={UNIT_PLANE}>
        <meshBasicMaterial
          map={map}
          color="#ffffff"
          toneMapped={false}
          transparent
          opacity={0}
          alphaTest={FRONT_LAYER_ALPHA_TEST}
        />
      </mesh>
      <mesh geometry={UNIT_PLANE}>
        <meshBasicMaterial
          map={map}
          color="#ffffff"
          toneMapped={false}
          transparent
          opacity={0}
          alphaTest={FRONT_LAYER_ALPHA_TEST}
          defines={{ USE_RGB_WHEEL_CENTER: 1 }}
          onBeforeCompile={onBeforeCompileCenterText}
        />
      </mesh>
      <mesh geometry={UNIT_PLANE}>
        <meshBasicMaterial
          map={map}
          color="#ffffff"
          toneMapped={false}
          transparent
          opacity={0}
          alphaTest={FRONT_LAYER_ALPHA_TEST}
          defines={{ USE_RGB_WHEEL_CENTER: 1 }}
          onBeforeCompile={onBeforeCompileCenterFull}
        />
      </mesh>

      {/* ── Center text + logo BACK (instanced) — map + alphaTest=0.025 ── */}
      <instancedMesh args={[UNIT_PLANE, undefined, 1]}>
        <meshBasicMaterial
          map={map}
          color="#ffffff"
          toneMapped={false}
          transparent
          opacity={0}
          alphaTest={CENTER_ALPHA_TEST}
          depthWrite={false}
        />
      </instancedMesh>
      <instancedMesh args={[UNIT_PLANE, undefined, 1]}>
        <meshBasicMaterial
          map={map}
          color="#ffffff"
          toneMapped={false}
          transparent
          opacity={0}
          alphaTest={CENTER_ALPHA_TEST}
          depthWrite={false}
          defines={{ USE_RGB_WHEEL_CENTER: 1 }}
          onBeforeCompile={compileBackText}
        />
      </instancedMesh>
      <instancedMesh args={[UNIT_PLANE, undefined, 1]}>
        <meshBasicMaterial
          map={map}
          color="#ffffff"
          toneMapped={false}
          transparent
          opacity={0}
          alphaTest={CENTER_ALPHA_TEST}
          depthWrite={false}
          defines={{ USE_RGB_WHEEL_CENTER: 1 }}
          onBeforeCompile={compileBackFull}
        />
      </instancedMesh>
    </group>
  );
}

/**
 * Logo center design. Subscribes to logo + contrast + transform slices.
 * Wraps `LogoImageLayers` (which internally caches the texture / contrast
 * halo) so a logo edit doesn't bubble up to MirrorGroup.
 */
function CenterLogoSection() {
  const { w, h, onBeforeCompileCenterFull } = useSceneShared();
  const designMode = useConfigurator((s) => s.designMode);
  const logoImageUrl = useConfigurator((s) => s.logoImageUrl);
  const logoAssetRevision = useConfigurator((s) => s.logoAssetRevision);
  const logoTransform = useConfigurator((s) => s.logoTransform);
  const logoRgb = useConfigurator((s) => s.logoRgb);
  const centerContrast = useConfigurator((s) => s.centerContrast);
  const brightness = useConfigurator((s) => s.brightness);
  const depthLayers = useConfigurator((s) => s.depthLayers);

  const effectiveLogoUrl = useMemo(() => {
    if (designMode !== "logo") return "";
    if (logoImageUrl?.trim()) return logoImageUrl;
    return getDefaultCenterLogoUrl();
  }, [designMode, logoImageUrl]);

  const groupRef = useRef<Group | null>(null);
  useLayoutEffect(() => {
    if (designMode !== "logo" || !effectiveLogoUrl || logoRgb) return;
    const white = new Color("#ffffff");
    groupRef.current?.traverse((obj) => {
      if (obj instanceof Mesh && obj.material instanceof MeshBasicMaterial) {
        obj.material.color.copy(white);
      }
    });
  }, [designMode, effectiveLogoUrl, logoRgb]);

  if (designMode !== "logo" || !effectiveLogoUrl) return null;
  return (
    <Suspense fallback={null}>
      <group ref={groupRef}>
        <LogoImageLayers
          key={`${effectiveLogoUrl}:${logoImageUrl ? logoAssetRevision : "maytrix-default"}`}
          url={effectiveLogoUrl}
          revision={logoImageUrl ? logoAssetRevision : ""}
          w={w}
          h={h}
          depthLayers={depthLayers}
          brightness={brightness}
          logoRgb={logoRgb}
          logoTransform={logoTransform}
          centerContrast={centerContrast}
          onBeforeCompileCenter={onBeforeCompileCenterFull}
        />
      </group>
    </Suspense>
  );
}

/**
 * Drives the RGB-wheel phase + LED-gain uniforms each frame. Reads the
 * relevant flags via `getState()` rather than React props — that way the
 * useFrame loop doesn't need to subscribe to anything and nothing in the
 * scene re-renders when only the hue/gain advance.
 */
function RgbDriver({
  rgbPhaseUniform,
  rgbLedGainUniform,
  ledSolidGainUniform,
  rgbAnimStyleUniform,
  rgbPaletteIndexUniform,
  ledEmissiveBoost,
}: {
  rgbPhaseUniform: { value: number };
  rgbLedGainUniform: { value: number };
  ledSolidGainUniform: { value: number };
  rgbAnimStyleUniform: { value: number };
  rgbPaletteIndexUniform: { value: number };
  ledEmissiveBoost: number;
}) {
  const boostRef = useRef(ledEmissiveBoost);
  useEffect(() => {
    boostRef.current = ledEmissiveBoost;
  }, [ledEmissiveBoost]);

  useFrame((_, dt) => {
    const s = getState();
    const wheelOn = s.rgb || s.textRgb || s.logoRgb || s.ledTextRgb || s.ledRailsRgb;
    if (wheelOn) {
      const tau = Math.PI * 2;
      rgbPhaseUniform.value = (rgbPhaseUniform.value + dt * 1.05 + tau) % tau;
    }
    // Frame-LED animation pattern — pushed into the shader uniform every
    // frame (cheap; lets us avoid a React re-render on style flips).
    rgbAnimStyleUniform.value = ANIM_STYLE_CODE[s.animationStyle] ?? ANIM_STYLE_CODE.classic;
    // Active palette row, clamped to a valid LUT index.
    const palette = Math.max(0, Math.min(PALETTE_COUNT - 1, s.rgbPalette | 0));
    rgbPaletteIndexUniform.value = palette;
    /**
     * LED gain — drives RGB-wheel and solid-color LED materials from a
     * single source (`brightness × ledEmissiveBoost`). Floor of 1 so
     * brightness/boost = 0 still renders the LED at its base color.
     */
    const ledGain = Math.max(1, s.brightness * boostRef.current);
    rgbLedGainUniform.value = ledGain;
    ledSolidGainUniform.value = ledGain;
  });
  return null;
}

function MirrorGroup({
  envMapIntensity,
  revealScaleT,
  ledEmissiveBoost,
}: {
  envMapIntensity: number;
  revealScaleT: number;
  /** Multiplier on top of `state.brightness` driving LED-material output
   *  above the bloom luminance threshold. Lifted from the parent `glow`
   *  leva controls so the bloom + LED gain stay tunable from one panel. */
  ledEmissiveBoost: number;
}) {
  // MirrorGroup subscribes ONLY to slices that determine shared geometry.
  // Text/logo/lighting/brightness changes flow through the section
  // components directly via `useConfigurator(...)` — MirrorGroup never
  // re-renders for those.
  const shape = useConfigurator((s) => s.shape);
  const size = useConfigurator((s) => s.size);
  const sizePresetShort = useConfigurator((s) => s.sizePresetShort);
  const sizeMode = useConfigurator((s) => s.sizeMode);
  const sizeUnit = useConfigurator((s) => s.sizeUnit);
  const sizeCustomWidth = useConfigurator((s) => s.sizeCustomWidth);
  const sizeCustomLength = useConfigurator((s) => s.sizeCustomLength);
  const depthLayers = useConfigurator((s) => s.depthLayers);
  const frameColor = useConfigurator((s) => s.frameColor);

  const rgbPhaseUniform = useMemo(() => ({ value: 0 }), []);
  const rgbLedGainUniform = useMemo(() => ({ value: 3.6 }), []);
  /**
   * Frame-LED animation pattern code (see `ANIM_STYLE_CODE` in
   * `rgbWheelShader.ts`). Mutated in place from `RgbDriver`'s `useFrame`,
   * keyed off `state.animationStyle`. Held as a uniform (not a re-render
   * trigger) so a style change costs zero React work — the shader picks up
   * the new value on the next draw.
   */
  const rgbAnimStyleUniform = useMemo(() => ({ value: ANIM_STYLE_CODE.classic }), []);
  /**
   * Active palette row (float so the shader can pass it as a plain uniform).
   * Updated in `RgbDriver` from `state.rgbPalette`; the same LUT texture
   * holds all `PALETTE_COUNT` rows, so a palette switch is just an index
   * uniform write — no texture rebuild.
   */
  const rgbPaletteIndexUniform = useMemo(() => ({ value: 0 }), []);
  /** Shared palette LUT texture (one row per palette, built once). */
  const rgbPaletteLutUniform = useMemo(() => ({ value: getPaletteLutTexture() }), []);
  /** Solid-color LED gain — multiplies `outgoingLight` of bar / point /
   *  wall-text / rail materials so they punch above the bloom luminance
   *  threshold and the post-pass renders a colored halo around them.
   *  Synced from `state.brightness × ledEmissiveBoost` in `useFrame`
   *  alongside `rgbLedGainUniform`. Initial value 1 = no boost (visible
   *  color = base `ledColor`), so a missing `useFrame` tick still
   *  renders the LED at its plain base color rather than blacking out. */
  const ledSolidGainUniform = useMemo(() => ({ value: 1 }), []);

  /**
   * UV sub-rect the center-RGB pattern is normalized into.
   *
   *  - `rgbCenterFullBoundsUniform` = (0,0,1,1): pattern spans the entire
   *    texture. Used for logo planes only (texture is the logo, plane is
   *    aspect-fitted via `logoPlaneSize`).
   *  - `rgbCenterTextBoundsUniform`: the actual rendered text block's UV
   *    bbox inside the (often-padded) canvas. Synced from
   *    `centerTextMap.userData.uvBounds` in CenterTextSection.
   */
  const rgbCenterFullBoundsUniform = useMemo(
    () => ({ value: new Vector4(0, 0, 1, 1) }),
    [],
  );
  const rgbCenterTextBoundsUniform = useMemo(
    () => ({ value: new Vector4(0, 0, 1, 1) }),
    [],
  );

  const onBeforeCompileLedBasic = useCallback(
    (shader: ShaderCompileParams) => {
      injectRgbWheelLedMeshBasic(
        shader,
        rgbPhaseUniform,
        rgbLedGainUniform,
        rgbAnimStyleUniform,
        rgbPaletteIndexUniform,
        rgbPaletteLutUniform,
        PALETTE_COUNT,
      );
    },
    [
      rgbPhaseUniform,
      rgbLedGainUniform,
      rgbAnimStyleUniform,
      rgbPaletteIndexUniform,
      rgbPaletteLutUniform,
    ],
  );

  const onBeforeCompileLedSolid = useCallback(
    (shader: ShaderCompileParams) => {
      injectLedSolidGain(shader, ledSolidGainUniform);
    },
    [ledSolidGainUniform],
  );

  const onBeforeCompileCenterText = useCallback(
    (shader: ShaderCompileParams) => {
      injectRgbWheelCenterShader(shader, rgbPhaseUniform, rgbCenterTextBoundsUniform);
    },
    [rgbPhaseUniform, rgbCenterTextBoundsUniform],
  );

  const onBeforeCompileCenterFull = useCallback(
    (shader: ShaderCompileParams) => {
      injectRgbWheelCenterShader(shader, rgbPhaseUniform, rgbCenterFullBoundsUniform);
    },
    [rgbPhaseUniform, rgbCenterFullBoundsUniform],
  );

  /**
   * Target aperture from the customizer (in world units). Frame, glass,
   * layers, cavity wall and LEDs are all built procedurally from this
   * aperture — single source of truth for the mirror's geometry.
   */
  const { w: targetW, h: targetH } = useMemo(
    () =>
      buildDimensionsFromState({
        shape,
        size,
        sizePresetShort,
        sizeMode,
        sizeUnit,
        sizeCustomWidth,
        sizeCustomLength,
      } as ConfiguratorState),
    [shape, size, sizePresetShort, sizeMode, sizeUnit, sizeCustomWidth, sizeCustomLength],
  );

  const worldHull: AperturePolygon = useMemo(
    () => buildApertureHull(shape, targetW, targetH),
    [shape, targetW, targetH],
  );

  const { w, h } = useMemo(() => {
    const { w: bw, h: bh } = polygonBBox(worldHull);
    return { w: Math.max(bw, 0.02), h: Math.max(bh, 0.02) };
  }, [worldHull]);

  const textLayoutCapsWH = useMemo(
    () => buildDimensions(shape, TEXT_LAYOUT_REF_INCH),
    [shape],
  );

  // backGeo / layerGeometries / glassGeo come from the session-level
  // geometry cache. clearGeometryCache fires on unmount to release them.
  const backGeo = useMemo(() => getCachedPolygonShapeGeometry(worldHull, 1), [worldHull]);
  const glassGeo = useMemo(
    () => getCachedPolygonShapeGeometry(worldHull, GLASS_INSET),
    [worldHull],
  );
  const layerGeometries = useMemo(() => {
    const n = depthLayers;
    return Array.from({ length: n }, (_, i) => {
      const t = layerProgress(i, n);
      const scale = layerScale(t);
      return getCachedPolygonShapeGeometry(worldHull, scale);
    });
  }, [depthLayers, worldHull]);
  const layerDepthSamples = useMemo(() => {
    const n = Math.max(1, depthLayers);
    return Array.from({ length: n }, (_, i) => {
      const t = layerProgress(i, n);
      return { i, t, z: layerZPos(t), scale: layerScale(t) };
    });
  }, [depthLayers]);
  const ledPointCount = useMemo(() => polygonLedPointCount(worldHull, 1), [worldHull]);

  // Cavity bounds — pure functions of the layer-progress endpoints, stable
  // regardless of state changes (`layerZPos`, `layerScale` are pure).
  const cavityZFront = layerZPos(1);
  const cavityZBack = layerZPos(0);
  const cavityScaleFront = layerScale(1);
  const nLayers = depthLayers;

  // Geometry caches are session-scoped — release everything on unmount.
  useEffect(() => () => clearGeometryCache(), []);

  const shared = useMemo<SceneShared>(
    () => ({
      worldHull,
      targetW,
      targetH,
      w,
      h,
      textLayoutCapsWH,
      layerGeometries,
      layerDepthSamples,
      backGeo,
      glassGeo,
      ledPointCount,
      nLayers,
      cavityZFront,
      cavityZBack,
      cavityScaleFront,
      onBeforeCompileLedBasic,
      onBeforeCompileLedSolid,
      onBeforeCompileCenterText,
      onBeforeCompileCenterFull,
      rgbCenterTextBoundsUniform,
    }),
    [
      worldHull,
      targetW,
      targetH,
      w,
      h,
      textLayoutCapsWH,
      layerGeometries,
      layerDepthSamples,
      backGeo,
      glassGeo,
      ledPointCount,
      nLayers,
      cavityZFront,
      cavityZBack,
      cavityScaleFront,
      onBeforeCompileLedBasic,
      onBeforeCompileLedSolid,
      onBeforeCompileCenterText,
      onBeforeCompileCenterFull,
      rgbCenterTextBoundsUniform,
    ],
  );

  // Phase 2 of the reveal: Z-scale tweens from near-flat (0.05) to 1.
  const REVEAL_Z_SCALE_FLAT = 0.05;
  const zScale = REVEAL_Z_SCALE_FLAT + revealScaleT * (1 - REVEAL_Z_SCALE_FLAT);

  return (
    <SceneSharedContext.Provider value={shared}>
      <group scale={[1, 1, zScale]}>
        <ProcFrameMesh
          shape={shape}
          innerW={targetW}
          innerH={targetH}
          apertureFrontZ={PORTAL_SURFACE_Z}
          frameColor={frameColor}
          envMapIntensity={envMapIntensity}
        />
        {/* Outer-scene precompile: covers the frame's PBR material + lights. */}
        <ScenePrecompiler />

        <mesh position={[0, 0, PORTAL_SURFACE_Z]} geometry={glassGeo}>
          <MeshPortalMaterial blend={0} resolution={896} side={DoubleSide}>
            <color attach="background" args={["#111111"]} />
            <ambientLight intensity={0.32} />
            <directionalLight position={[2, 3, 4]} intensity={1.15} />
            <directionalLight position={[-2, -1, 2]} intensity={0.28} />
            <group position={[0, 0, -PORTAL_SURFACE_Z]}>
              <mesh geometry={backGeo} position={[0, 0, -MIRROR_VIRTUAL_DEPTH + 0.01]}>
                <meshStandardMaterial color="#050505" roughness={0.2} metalness={0.8} />
              </mesh>
              <DepthPlanesSection />
              <WallTextSection />
              <CenterTextSection />
              <CenterLogoSection />
              <BarContourSection />
              <LedPointSection />
              <ShaderWarmupGroup />
              <RgbDriver
                rgbPhaseUniform={rgbPhaseUniform}
                rgbLedGainUniform={rgbLedGainUniform}
                ledSolidGainUniform={ledSolidGainUniform}
                rgbAnimStyleUniform={rgbAnimStyleUniform}
                rgbPaletteIndexUniform={rgbPaletteIndexUniform}
                ledEmissiveBoost={ledEmissiveBoost}
              />
              {/* Portal-scene precompile: covers depth planes, wall + center
                  text, bar/point LEDs, logo, and every RGB variant the
                  warmup group above mounts off-frustum. */}
              <ScenePrecompiler />
            </group>
          </MeshPortalMaterial>
        </mesh>
      </group>
    </SceneSharedContext.Provider>
  );
}


/**
 * Producer side of the cart-image bridge — see `viewer/src/snapshotBridge.ts`.
 *
 * Lives inside the `<Canvas>` so it has direct access to `gl` and `scene`.
 * On every snapshot request (driven by CPB's `Stage.toCanvas()` interception
 * in `cpbSnapshotHijack.ts`), we render ONE extra frame from a fixed
 * head-on camera straight into the WebGL back buffer, then `drawImage` that
 * back buffer onto the caller's target 2D canvas. R3F's frame loop will
 * overpaint with the regular cursor-steer view on the next tick (~16 ms),
 * so the user-visible disruption is, at most, a single frame of pure scene
 * geometry without bloom — usually invisible because the user has already
 * clicked Add-to-Cart and is being redirected/drawer-opened by the time
 * any compositor pass runs.
 *
 * We deliberately bypass `<EffectComposer>` here. Bloom blooms great
 * fullscreen but reads as smudgy at thumbnail sizes, and the CPB-uploaded
 * `_image` is rendered tiny in cart UI / order admin — clean geometry +
 * material lighting beats halated geometry.
 */
function SnapshotProvider() {
  const { gl, scene, camera } = useThree();

  useEffect(() => {
    // Reused across calls — Three.js cameras are cheap-but-not-free and we
    // don't want a per-snapshot allocation showing up in profiles.
    const snapshotCamera = new PerspectiveCamera(40, 1, 0.01, 100);

    return registerSnapshotProvider((target) => {
      const dstW = target.width;
      const dstH = target.height;
      if (!dstW || !dstH) return;

      const src = gl.domElement;
      const srcW = src.width;
      const srcH = src.height;
      if (!srcW || !srcH) return;

      // Match CursorSteerCamera's DEFAULT view (theta=0, phi=DEFAULT_PHI) —
      // NOT pure head-on at Y=0. Scene contents (centre text plane, depth
      // layers, wall-text band) are positioned to read centred from that
      // slightly-elevated default angle; rendering from (0, 0, distance)
      // would expose the geometric centre offset that the design hides
      // under that tilt, and the MAYTRIX text would float low in the cart
      // PNG while the frame stays centred.
      //
      // `theta=0` flattens any user-applied orbit so two customers who
      // configured identically get identical snapshots; `phi=DEFAULT_PHI`
      // keeps the visual centring the page is designed around. Target
      // `(0, 0, -0.1)` matches LOOK_AT in CursorSteerCamera so frame +
      // portal share the same optical axis.
      const SNAPSHOT_PHI = Math.PI / 2 - 0.18; // mirror CursorSteerCamera's DEFAULT_PHI
      const distance = camera.position.length() || 3.4;
      const camY = distance * Math.cos(SNAPSHOT_PHI);
      const camZ = distance * Math.sin(SNAPSHOT_PHI);
      snapshotCamera.position.set(0, camY, camZ);
      snapshotCamera.lookAt(0, 0, -0.1);
      snapshotCamera.aspect = srcW / srcH;
      snapshotCamera.fov = "fov" in camera ? camera.fov : 40;
      snapshotCamera.updateProjectionMatrix();

      // Synchronous render into gl.domElement (the same canvas R3F manages).
      //
      // EffectComposer toggles `gl.autoClear = false` and leaves
      // `gl.setRenderTarget(…)` pointed at one of its internal ping-pong
      // targets between frames. If we don't reset those here, our extra
      // render either (a) writes into the composer's internal target
      // rather than the screen canvas, OR (b) draws over the previous
      // composer-composed frame without clearing — producing the
      // ghost / double-exposure look (orbit angle + head-on overlayed)
      // we used to see in the cart `_image` PNG.
      //
      // Snapshot the renderer state, render fresh head-on into the
      // screen canvas with a clean clear, then restore so the next R3F
      // tick continues with the composer pipeline untouched.
      const prevTarget = gl.getRenderTarget();
      const prevAutoClear = gl.autoClear;
      gl.setRenderTarget(null);
      gl.autoClear = true;
      gl.clear(true, true, true);
      gl.render(scene, snapshotCamera);
      gl.autoClear = prevAutoClear;
      gl.setRenderTarget(prevTarget);

      const ctx = target.getContext("2d");
      if (!ctx) return;

      // Letterbox-fit the WebGL frame onto the target rectangle. CPB doesn't
      // tell us the aspect it'll request — could be 1:1 (default Konva
      // stage), the live preview's container ratio, or a pixelRatio multiple.
      // Stretching the scene would distort the mirror silhouette; black bars
      // are honest and look intentional in cart thumbnails.
      const srcAspect = srcW / srcH;
      const dstAspect = dstW / dstH;
      let drawW = dstW;
      let drawH = dstH;
      let drawX = 0;
      let drawY = 0;
      if (srcAspect > dstAspect) {
        drawH = Math.round(dstW / srcAspect);
        drawY = Math.round((dstH - drawH) / 2);
      } else {
        drawW = Math.round(dstH * srcAspect);
        drawX = Math.round((dstW - drawW) / 2);
      }
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, dstW, dstH);
      ctx.drawImage(src, drawX, drawY, drawW, drawH);
    });
  }, [gl, scene, camera]);

  return null;
}

/** Orbit from pointer / touch; zoom is locked (fit radius). On fine pointers, the WebGL canvas stays `pointer-events:none` so wheel uses native page scroll. */
function CursorSteerCamera() {
  const { camera, gl, size } = useThree();
  // Camera fit only depends on the aperture-dimension slices. Subscribing
  // individually keeps unrelated state changes (color, brightness, text…)
  // from re-running the fitRadius memo / snap effect.
  const shape = useConfigurator((s) => s.shape);
  const sizeInch = useConfigurator((s) => s.size);
  const sizePresetShort = useConfigurator((s) => s.sizePresetShort);
  const sizeMode = useConfigurator((s) => s.sizeMode);
  const sizeUnit = useConfigurator((s) => s.sizeUnit);
  const sizeCustomWidth = useConfigurator((s) => s.sizeCustomWidth);
  const sizeCustomLength = useConfigurator((s) => s.sizeCustomLength);
  const pointer = useRef({ nx: 0.5, ny: 0.5, inside: false });
  const spherical = useRef({ r: 3.5, theta: 0, phi: Math.PI / 2 - 0.1 });
  const target = useRef({ r: 3.5, theta: 0, phi: Math.PI / 2 - 0.1 });
  const lastInputTs = useRef<number>(typeof performance !== "undefined" ? performance.now() : 0);
  const touchState = useRef<{
    mode: "none" | "rotate" | "pinch";
    x: number;
    y: number;
    pinchDistance: number;
  }>({ mode: "none", x: 0, y: 0, pinchDistance: 0 });

  // `theta = 0` keeps the camera perpendicular to the mirror face; cursor-steer
  // adds ±THETA_LIMIT sway around that. `phi` just under π/2 puts the camera
  // slightly above the mirror so we read it as "top-down" (stronger than the
  // old 0.1 rad ≈ 6° — 0.18 rad ≈ 10° gives a more pronounced hero angle
  // without clipping through the portal glass plane).
  const DEFAULT_THETA = 0;
  const DEFAULT_PHI = Math.PI / 2 - 0.18;
  // Slightly widened sway limits (was 0.6 / -0.35 / +0.03). Gives the hover
  // a touch more "presence" without letting the camera clip the portal plane.
  const THETA_LIMIT = 0.75;
  const PHI_MIN = Math.PI / 2 - 0.4;
  const PHI_MAX = Math.PI / 2 + 0.08;

  /**
   * Input gate — cursor-driven tilt is ignored until the entrance reveal has
   * finished. Before that, the camera holds its default (theta=0, phi=DEFAULT)
   * so the first thing the user sees is the mirror head-on rather than a
   * half-tilted rotation mid-animation.
   */
  const cursorEnabledRef = useRef(isRevealComplete());
  useEffect(() => {
    if (cursorEnabledRef.current) return;
    return onRevealComplete(() => {
      cursorEnabledRef.current = true;
    });
  }, []);
  /**
   * Camera looks at this point. Kept at (0, 0, −0.1) so the mirror center
   * renders at screen center. The top-down read comes from `DEFAULT_PHI` — the
   * camera sits above the equator (Y ≈ 0.6 at r=3.5), and since it looks AT
   * origin, the optical axis tilts down naturally. Lifting the target Y would
   * shift the mirror off-center, which is not what we want here.
   */
  const LOOK_AT = useMemo(() => [0, 0, -0.1] as const, []);

  /**
   * Minimum distance from the mirror at which its full outline (plus a small
   * padding for frame thickness) fits inside the camera frustum on both axes.
   * Recomputed on window resize (aspect changes) and on mirror shape/size
   * changes — the only events that can make the mirror overflow the viewport.
   *
   * 15 % safety margin so edges never touch the frustum exactly, which would
   * clip under slight cursor-steer rotation or when perspective skews corners
   * outward. Zoom is not user-controllable; this is the only source of `r`.
   */
  const fitRadius = useMemo(() => {
    const { w, h } = buildDimensionsFromState({
      shape,
      size: sizeInch,
      sizePresetShort,
      sizeMode,
      sizeUnit,
      sizeCustomWidth,
      sizeCustomLength,
    } as ConfiguratorState);
    const framePad = 0.22;
    const targetW = w + framePad;
    const targetH = h + framePad;
    const aspect = Math.max(size.width / Math.max(size.height, 1), 1e-3);
    const perspectiveFov = "fov" in camera ? camera.fov : 45;
    const vFov = MathUtils.degToRad(perspectiveFov);
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
    const distV = targetH / 2 / Math.tan(vFov / 2);
    const distH = targetW / 2 / Math.tan(hFov / 2);
    return Math.max(distV, distH) * 1.15;
  }, [
    camera,
    size.height,
    size.width,
    shape,
    sizeInch,
    sizePresetShort,
    sizeMode,
    sizeUnit,
    sizeCustomWidth,
    sizeCustomLength,
  ]);

  const clampTarget = useCallback(() => {
    target.current.theta = Math.max(-THETA_LIMIT, Math.min(THETA_LIMIT, target.current.theta));
    target.current.phi = Math.max(PHI_MIN, Math.min(PHI_MAX, target.current.phi));
    // r is not clamped — it's locked to fitRadius via the fit effect below.
  }, [PHI_MAX, PHI_MIN, THETA_LIMIT]);

  const syncCamera = useCallback(() => {
    const s = spherical.current;
    const x = s.r * Math.sin(s.phi) * Math.sin(s.theta);
    const y = s.r * Math.cos(s.phi);
    const z = s.r * Math.sin(s.phi) * Math.cos(s.theta);
    camera.position.set(x, y, z);
    camera.lookAt(LOOK_AT[0], LOOK_AT[1], LOOK_AT[2]);
  }, [camera, LOOK_AT]);

  // Keep both the animated `spherical.r` and its `target.r` pinned to the
  // computed fit distance. Snapping spherical immediately avoids a one-time
  // zoom animation on mount and on every resize / shape change.
  useEffect(() => {
    target.current.r = fitRadius;
    spherical.current.r = fitRadius;
    syncCamera();
  }, [fitRadius, syncCamera]);

  useEffect(() => {
    const el = gl.domElement;

    /** Fine pointer → canvas is CSS `pointer-events:none`; track hover via coordinates. */
    const finePointer =
      typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)")?.matches === true;

    const setFromEvent = (clientX: number, clientY: number) => {
      const rect = el.getBoundingClientRect();
      pointer.current.nx = (clientX - rect.left) / Math.max(rect.width, 1);
      pointer.current.ny = (clientY - rect.top) / Math.max(rect.height, 1);
    };

    let offPointerTracking: () => void;

    if (finePointer) {
      const winPointerMove = (e: PointerEvent) => {
        if (e.pointerType === "touch") return;
        const rect = el.getBoundingClientRect();
        const inside =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;
        pointer.current.inside = inside;
        if (inside) setFromEvent(e.clientX, e.clientY);
        lastInputTs.current = performance.now();
      };
      window.addEventListener("pointermove", winPointerMove, { passive: true });
      offPointerTracking = () => window.removeEventListener("pointermove", winPointerMove);
    } else {
      const onPointerMove = (e: PointerEvent) => {
        if (e.pointerType === "touch") return;
        setFromEvent(e.clientX, e.clientY);
        lastInputTs.current = performance.now();
      };
      const onPointerEnter = (e: PointerEvent) => {
        if (e.pointerType === "touch") return;
        pointer.current.inside = true;
        setFromEvent(e.clientX, e.clientY);
        lastInputTs.current = performance.now();
      };
      const onPointerLeave = () => {
        pointer.current.inside = false;
        lastInputTs.current = performance.now();
      };

      el.addEventListener("pointermove", onPointerMove);
      el.addEventListener("pointerenter", onPointerEnter);
      el.addEventListener("pointerleave", onPointerLeave);
      el.addEventListener("pointercancel", onPointerLeave);
      offPointerTracking = () => {
        el.removeEventListener("pointermove", onPointerMove);
        el.removeEventListener("pointerenter", onPointerEnter);
        el.removeEventListener("pointerleave", onPointerLeave);
        el.removeEventListener("pointercancel", onPointerLeave);
      };
    }

    const touchDistance = (t0: Touch, t1: Touch) => {
      const dx = t1.clientX - t0.clientX;
      const dy = t1.clientY - t0.clientY;
      return Math.hypot(dx, dy);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const t = e.touches[0];
        touchState.current = {
          mode: "rotate",
          x: t.clientX,
          y: t.clientY,
          pinchDistance: 0,
        };
        e.preventDefault();
      } else if (e.touches.length >= 2) {
        touchState.current = {
          mode: "pinch",
          x: 0,
          y: 0,
          pinchDistance: touchDistance(e.touches[0], e.touches[1]),
        };
        e.preventDefault();
      }
      lastInputTs.current = performance.now();
    };

    const onTouchMove = (e: TouchEvent) => {
      if (
        cursorEnabledRef.current &&
        e.touches.length === 1 &&
        touchState.current.mode === "rotate"
      ) {
        const t = e.touches[0];
        const dx = t.clientX - touchState.current.x;
        const dy = t.clientY - touchState.current.y;
        touchState.current.x = t.clientX;
        touchState.current.y = t.clientY;

        target.current.theta += dx * 0.0045;
        target.current.phi += dy * 0.0035;
        clampTarget();
        e.preventDefault();
      }
      // Two-finger pinch intentionally ignored — zoom is locked to fitRadius
      // (mirror always fills the viewport). Single-finger orbit stays active.
      lastInputTs.current = performance.now();
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const t = e.touches[0];
        touchState.current = {
          mode: "rotate",
          x: t.clientX,
          y: t.clientY,
          pinchDistance: 0,
        };
      } else if (e.touches.length >= 2) {
        touchState.current = {
          mode: "pinch",
          x: 0,
          y: 0,
          pinchDistance: touchDistance(e.touches[0], e.touches[1]),
        };
      } else {
        touchState.current = { mode: "none", x: 0, y: 0, pinchDistance: 0 };
      }
      lastInputTs.current = performance.now();
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    el.addEventListener("touchcancel", onTouchEnd, { passive: false });

    syncCamera();

    return () => {
      offPointerTracking();
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [clampTarget, gl, syncCamera]);

  useFrame(() => {
    const now = performance.now();
    const touchMode = touchState.current.mode;
    const isTouchInteracting = touchMode === "rotate" || touchMode === "pinch";
    const isDesktopHover = pointer.current.inside && touchMode === "none";
    const cursorEnabled = cursorEnabledRef.current;

    if (cursorEnabled && isDesktopHover) {
      const { nx, ny } = pointer.current;
      target.current.theta = DEFAULT_THETA + (nx - 0.5) * 2 * THETA_LIMIT;
      const phiSpan = (PHI_MAX - PHI_MIN) / 2;
      const phiCenter = (PHI_MAX + PHI_MIN) / 2;
      target.current.phi = phiCenter + (ny - 0.5) * 2 * phiSpan;
      clampTarget();
      lastInputTs.current = now;
    } else if (!isTouchInteracting) {
      target.current.theta += (DEFAULT_THETA - target.current.theta) * 0.14;
      target.current.phi += (DEFAULT_PHI - target.current.phi) * 0.14;
      clampTarget();
    }

    const s = spherical.current;
    const ease = 0.1;
    s.theta += (target.current.theta - s.theta) * ease;
    s.phi += (target.current.phi - s.phi) * ease;
    // r already pinned to fitRadius via effect; ease keeps it smooth on resize.
    s.r += (target.current.r - s.r) * 0.2;

    syncCamera();
  });

  return null;
}

/**
 * Show dev tooling (Leva glow tuner, Stats FPS counter) only in dev builds.
 * `import.meta.env.DEV` is inlined by Vite to `true` in `vite build --watch` /
 * `vite dev`, `false` in `vite build`. This keeps the public Shopify bundle
 * free of the debug overlay while still allowing quick tuning locally.
 *
 * A URL override (`?viewer_debug=1`) lets us flip tooling on from a live
 * preview without a rebuild — useful for remote debugging on a pushed dev
 * theme without exposing it to end customers.
 */
const SHOW_DEV_TOOLING =
  import.meta.env.DEV ||
  (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("viewer_debug"));

/*
  Entrance reveal orchestration.

  Timeline:
    t = 0              — CPB preloader is hidden (`onCpbLoaderHidden` fires)
    t = 1000 ms        — add the `show-canvas` class on `.r3f-canvas`, start
                         the 800 ms tween. Both bloom params and model Z-scale
                         ease from 0 → 1 in lock-step.
    t = 1800 ms        — tween done. `markRevealComplete()` fires, which
                         releases `CursorSteerCamera`'s mouse-tilt gate.

  `bloomT` multiplies every Bloom prop (intensity, threshold, smoothing,
  radius, ledEmissiveBoost), so at t=0 bloom is effectively off; `scaleT`
  drives the Z-compression of the mirror group.
*/
const REVEAL_DELAY_MS = 1000;
/** Pause between attaching `.show-canvas` and starting the bloom/scale tween. */
const REVEAL_PRE_TWEEN_DELAY_MS = 0;
const REVEAL_DURATION_MS = 2000;

/**
 * Exponential ease-out — rises fast, then decays asymptotically into the
 * target. Reads as a "damped" settle (value ramps up sharply, then slowly
 * eases into place) — the character the brief asks for. No overshoot, so no
 * risk of bloom-intensity flickering above leva targets mid-tween.
 *
 * At t=0: 0. At t=0.1: ≈0.5. At t=0.3: ≈0.87. At t=0.5: ≈0.97. At t=1: 1.
 */
const easeOutExpo = (t: number) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));

function useRevealProgress(): { bloomT: number; scaleT: number; bloomPulse: number } {
  const initial = isRevealComplete() ? 1 : 0;
  const [bloomT, setBloomT] = useState(initial);
  const [scaleT, setScaleT] = useState(initial);
  /**
   * Multiplier applied only to `intensity` after the reveal completes. Two
   * coprime sines give a non-periodic feel, damped amplitude keeps the pulse
   * "subtle" — ±~9% peak-to-peak at most. Starts at 1 (no effect) during the
   * reveal tween so the two effects don't fight each other.
   */
  const [bloomPulse, setBloomPulse] = useState(1);

  useEffect(() => {
    let cancelled = false;
    let rafId: number | null = null;
    let classTimeout: number | null = null;
    let tweenTimeout: number | null = null;
    let startTime: number | null = null;
    let pulseStartTime: number | null = null;

    const pulseTick = () => {
      if (cancelled) return;
      if (pulseStartTime === null) pulseStartTime = performance.now();
      const dt = (performance.now() - pulseStartTime) / 1000;
      const p = 1 + 0.12 * Math.sin(dt * 1.7) + 0.06 * Math.sin(dt * 4.3 + 1.2);
      setBloomPulse(p);
      rafId = requestAnimationFrame(pulseTick);
    };

    const tweenTick = () => {
      if (cancelled) return;
      const now = performance.now();
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / REVEAL_DURATION_MS);
      const eased = easeOutExpo(t);
      setBloomT(eased);
      setScaleT(eased);
      if (elapsed < REVEAL_DURATION_MS) {
        rafId = requestAnimationFrame(tweenTick);
      } else {
        markRevealComplete();
        rafId = requestAnimationFrame(pulseTick);
      }
    };

    // Session that hot-reloaded after reveal already done: skip straight to pulse.
    if (isRevealComplete()) {
      rafId = requestAnimationFrame(pulseTick);
      return () => {
        cancelled = true;
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    }

    const unsubscribe = onCpbLoaderHidden(() => {
      if (cancelled) return;
      // Step 1 (at REVEAL_DELAY_MS after loader hide): attach the class.
      classTimeout = window.setTimeout(() => {
        if (cancelled) return;
        const canvas = document.querySelector<HTMLElement>(".r3f-canvas");
        if (canvas) canvas.classList.add("show-canvas");
        // Step 2 (after an additional pre-tween pause): begin the rAF tween.
        tweenTimeout = window.setTimeout(() => {
          if (cancelled) return;
          rafId = requestAnimationFrame(tweenTick);
        }, REVEAL_PRE_TWEEN_DELAY_MS);
      }, REVEAL_DELAY_MS);
    });

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (classTimeout !== null) window.clearTimeout(classTimeout);
      if (tweenTimeout !== null) window.clearTimeout(tweenTimeout);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { bloomT, scaleT, bloomPulse };
}

function ConfiguratorCanvas() {

  /*
    Top-of-panel switch: pick which post-process drives the "lighting" look.
    The first `useControls` call lands at the root of Leva (no folder), so
    this selector shows at the very top-left. Values match the IDs in the
    conditional render inside `<EffectComposer>`, and drive `render` on
    every control in the effect-specific folders below — so the Glow folder
    appears only in Bloom mode and LensFlare folder only in LensFlare mode.
  */
  const { effect } = useControls({
    effect: {
      value: "bloom" as const,
      options: ["bloom", "lensFlare"],
      label: "Effect",
    },
  });

  // Hide-per-effect helper. Spread into any control's schema to bind its
  // visibility to the top switch. Leva re-evaluates `render` on every store
  // change, so the panel reshapes itself without a reload.
  const onlyInBloom = { render: (get: (path: string) => unknown) => get("effect") === "bloom" };
  const onlyInLensFlare = {
    render: (get: (path: string) => unknown) => get("effect") === "lensFlare",
  };

  /*
    Bloom + LED-emissive tuning. `useControls` is called unconditionally so
    its default values drive bloom even when the Leva panel is hidden (prod).
    Real glow: every solid / RGB LED material (bar tubes, point discs,
    wall-text bands, rails) has an `onBeforeCompile` that multiplies
    `outgoingLight` by `state.brightness × ledEmissiveBoost`, pushing the
    rendered luminance above the bloom threshold while the tone-mapped
    frame / glass surfaces stay clamped. `mipmapBlur` gives a soft halo
    without a separate render target per frame.
  */
  const glow = useControls("Glow", {
    intensity: { value: 1.45, min: 0, max: 5, step: 0.05, ...onlyInBloom },
    /* Above ~1.0 keeps metal frame glints mostly out of bloom — otherwise mipmap blur bleeds past the rim silhouette. */
    luminanceThreshold: { value: 1.08, min: 0, max: 2, step: 0.01, ...onlyInBloom },
    luminanceSmoothing: { value: 0.28, min: 0, max: 1, step: 0.01, ...onlyInBloom },
    radius: { value: 0.0, min: 0, max: 1, step: 0.01, ...onlyInBloom },
    /* Direct multiplier on LED material `outgoingLight` (bar tubes, point
       discs, wall-text bands, rails). The bloom pass uses
       luminanceThreshold≈1.08 with smoothing 0.28, so white-ish LED
       output catches bloom around `gain ≈ 1.0`. ~1.5 puts
       low-luminance hues (e.g. cyan #42d4ff, L≈0.72) at ~1.1 — just past
       the smoothing window for a visible halo without nuking the scene
       (text-lighting renders the wall bands across 10+ depth layers, so
       their bloom contribution stacks fast and a higher boost easily
       washes the gold frame and surrounding scene). */
    ledEmissiveBoost: { value: 1.5, min: 0.5, max: 6, step: 0.1, ...onlyInBloom },
    // Render-target resolution for the bloom pass, relative to the canvas.
    // 0.5 = half-res (cheaper, softer); 1.0 = native (crisper, costlier).
    resolutionScale: { value: 0.72, min: 0.1, max: 1, step: 0.05, ...onlyInBloom },
    // Mip levels for `mipmapBlur` — lower = tighter halo (less spill past frame edges).
    levels: { value: 4, min: 3, max: 10, step: 1, ...onlyInBloom },
  }) as GlowParams;

  const lights = useControls("Lighting", {
    envPreset: {
      value: "warehouse" as const,
      options: ["studio", "warehouse", "city", "apartment", "sunset", "dawn", "night", "forest", "park", "lobby"],
    },
    envIntensity: { value: 0.72, min: 0, max: 2, step: 0.01 },
    envMapOnFrame: { value: 0.78, min: 0, max: 3, step: 0.05 },
    ambient: { value: 0.12, min: 0, max: 1, step: 0.01 },
  });

  // Canvas visibility gate is currently off — scene is visible immediately.
  // (was: held invisible until `onCpbLoaderHidden` to avoid a one-frame flash
  // before CPB's own preloader covered the page.)

  /*
    LensFlare controls — only consumed when `effect === 'lensFlare'`. Left
    unconditionally declared so Leva's state / persisted values don't thrash
    when the user flips the top switch.

    NOTE: tuning LensFlare values will briefly black-flash the composer on
    each change because @react-three/postprocessing rebuilds the effect on
    any prop change (see wrapEffect's `JSON.stringify(props)` memo dep).
    That's inherent to the library version pinned in package.json.
  */
  const flare = useControls("LensFlare", {
    enabled: { value: true, ...onlyInLensFlare },
    lensPositionX: { value: 2.0, min: -5, max: 5, step: 0.05, ...onlyInLensFlare },
    lensPositionY: { value: 2.6, min: -5, max: 5, step: 0.05, ...onlyInLensFlare },
    lensPositionZ: { value: 0.6, min: -5, max: 5, step: 0.05, ...onlyInLensFlare },
    opacity: { value: 1, min: 0, max: 1, step: 0.01, ...onlyInLensFlare },
    glareSize: { value: 0.35, min: 0, max: 0.8, step: 0.005, ...onlyInLensFlare },
    flareSize: { value: 0.004, min: 0, max: 0.05, step: 0.0005, ...onlyInLensFlare },
    flareSpeed: { value: 0.4, min: 0, max: 2, step: 0.05, ...onlyInLensFlare },
    flareShape: { value: 0.1, min: 0, max: 1, step: 0.01, ...onlyInLensFlare },
    haloScale: { value: 0.5, min: 0, max: 2, step: 0.01, ...onlyInLensFlare },
    ghostScale: { value: 0.1, min: 0, max: 1, step: 0.01, ...onlyInLensFlare },
    starPoints: { value: 6, min: 0, max: 12, step: 1, ...onlyInLensFlare },
    starBurst: { value: true, ...onlyInLensFlare },
    animated: { value: true, ...onlyInLensFlare },
    anamorphic: { value: false, ...onlyInLensFlare },
    secondaryGhosts: { value: true, ...onlyInLensFlare },
    aditionalStreaks: { value: true, ...onlyInLensFlare },
    colorGain: { value: "#fff1dc", ...onlyInLensFlare },
  });

  const flareLensPosition = useMemo(
    () => new Vector3(flare.lensPositionX, flare.lensPositionY, flare.lensPositionZ),
    [flare.lensPositionX, flare.lensPositionY, flare.lensPositionZ],
  );
  const flareColorGain = useMemo(() => new Color(flare.colorGain), [flare.colorGain]);

  const { bloomT, scaleT, bloomPulse } = useRevealProgress();

  return (
    <>
      {/* Leva is fully hidden in production builds (and on any non-debug
          page load) — `import.meta.env.DEV` is the vite-dev flag, plus an
          opt-in `?viewer_debug` URL escape hatch so we can still inspect
          a deployed build in-place. The underlying `useControls` hooks
          remain registered unconditionally so their default values flow
          into bloom / lighting / flare even when the UI panel doesn't
          render at all.

          DO NOT remove `<Leva>` itself — without it, hot-reload-time
          updates to control defaults can desync from the persisted Leva
          store in localStorage and produce silently-wrong lighting on
          subsequent loads. The gated `hidden` prop is the right knob. */}
      <Leva collapsed hidden={!SHOW_DEV_TOOLING} />
      <Canvas
        className="r3f-canvas"
        // CursorSteerCamera overrides position every frame; initial value just
        // avoids a one-frame flash at (0,0,0) before that kicks in. `fov: 40`
        // (was 45) is slightly more telephoto — flatters the mirror silhouette
        // without distorting edges.
        camera={{ position: [0, 0.6, 3.4], fov: 40, near: 0.01, far: 100 }}
        // `preserveDrawingBuffer: true` keeps the WebGL back buffer readable
        // between presents — required so SnapshotProvider's drawImage(gl.domElement, …)
        // copy reliably gets the rendered pixels (otherwise the canvas reads
        // back as cleared/transparent on most drivers after the browser
        // composites a frame).
        gl={{
          antialias: true,
          alpha: false,
          powerPreference: "high-performance",
          preserveDrawingBuffer: true,
        }}
        onCreated={({ gl }) => {
          gl.setClearColor("#111111");
          gl.outputColorSpace = SRGBColorSpace;
        }}
      >
        <CursorSteerCamera />
        <SnapshotProvider />
        <LightStyleOptimisticListener />

        <Suspense fallback={null}>
          <Environment
            preset={lights.envPreset as EnvPreset}
            background={false}
            environmentIntensity={lights.envIntensity}
            blur={0.58}
            environmentRotation={[0, 0.68, 0]}
          />
        </Suspense>

        <ambientLight intensity={lights.ambient} />
        <hemisphereLight args={["#faf6ff", "#504238"]} intensity={0.32} />
        {/* Low grazing fill so vertical frame faces pick up grain / metal read (not only top strip). */}
        <directionalLight position={[-4.5, 0.2, 3.2]} intensity={0.17} color="#e8ddd4" />
        <directionalLight position={[4.5, 0.2, 3.2]} intensity={0.17} color="#dbe0e8" />
        <directionalLight position={[0.25, 0.35, 4.6]} intensity={0.04} color="#fff6ec" />
        <directionalLight position={[-0.45, -0.2, 4.25]} intensity={0.09} color="#ffe8d4" />
        <pointLight position={[0, 0.15, 4.05]} intensity={0.17} distance={28} decay={2} color="#ffeedd" />
        <pointLight position={[-3.6, 1.5, 5.0]} intensity={0.11} distance={36} decay={2} color="#ffe8d0" />
        <pointLight position={[3.6, 1.5, 5.0]} intensity={0.11} distance={36} decay={2} color="#e8ecff" />
        {/* Top key — narrow cone so the rail highlight stays a small pool (was flooding ~full width). */}
        <spotLight
          position={[0.05, 4.05, 5.2]}
          angle={Math.PI / 6.2}
          penumbra={0.52}
          intensity={0.34}
          distance={34}
          decay={2}
          color="#e8cf98"
        >
          <object3D position={[0.06, 0.92, 0]} attach="target" />
        </spotLight>
        <spotLight
          position={[-3.35, -2.65, 5.05]}
          angle={Math.PI / 2.15}
          penumbra={1}
          intensity={0.48}
          distance={32}
          decay={2}
          color="#efd7a0"
        >
          <object3D position={[-0.72, -0.68, 0]} attach="target" />
        </spotLight>
        <SceneErrorBoundary>
          {/* `envMapIntensity` is unaffected by the reveal tween — scene
              lighting is already at full leva targets from the first frame
              (only the bloom post-pass is gated by `bloomT`). */}
          <MirrorGroup
            envMapIntensity={lights.envMapOnFrame}
            revealScaleT={scaleT}
            ledEmissiveBoost={glow.ledEmissiveBoost * bloomT}
          />
        </SceneErrorBoundary>

        <AdaptiveDpr pixelated={false} />
        {/*
          `multisampling` controls MSAA sample count on the composer's
          internal render targets. With `<SMAA />` doing image-space AA on
          the final pass, full MSAA=4 is largely redundant and roughly
          quadruples per-pixel sample cost on weaker GPUs (mid-range mobile,
          integrated laptops). MSAA=2 keeps thin LED edges from aliasing
          before the SMAA pass cleans them up — close to MSAA=4 visual
          quality, ~2× cheaper.
        */}
        <EffectComposer multisampling={2}>
          {effect === "bloom" ? (
            /* All four params ride `bloomT` during the reveal tween (0 →
               target). After the tween completes, `bloomPulse` breathes
               `intensity` alone by ±~9% — threshold/smoothing/radius stay
               static so the edge of what bloom catches doesn't flicker. */
            <Bloom
              intensity={glow.intensity * bloomT * bloomPulse}
              luminanceThreshold={glow.luminanceThreshold * bloomT}
              luminanceSmoothing={glow.luminanceSmoothing * bloomT}
              radius={glow.radius * bloomT}
              resolutionScale={glow.resolutionScale}
              levels={glow.levels}
              mipmapBlur
            />
          ) : (
            <LensFlare
              enabled={flare.enabled}
              lensPosition={flareLensPosition}
              opacity={flare.opacity}
              glareSize={flare.glareSize}
              flareSize={flare.flareSize}
              flareSpeed={flare.flareSpeed}
              flareShape={flare.flareShape}
              haloScale={flare.haloScale}
              ghostScale={flare.ghostScale}
              starPoints={flare.starPoints}
              starBurst={flare.starBurst}
              animated={flare.animated}
              anamorphic={flare.anamorphic}
              secondaryGhosts={flare.secondaryGhosts}
              aditionalStreaks={flare.aditionalStreaks}
              colorGain={flareColorGain}
            />
          )}
          <SMAA />
        </EffectComposer>
        {SHOW_DEV_TOOLING && <Stats />}
      </Canvas>
    </>
  );
}

/**
 * Canvas takes no props — it pulls every relevant state slice via the store
 * directly through MirrorGroup / CursorSteerCamera / Sections. The previous
 * `memo(state)` wrapper was a no-op since App handed a fresh `state` ref on
 * every patch; isolating subscriptions per Section accomplishes the same
 * "only re-render when relevant" goal much more granularly.
 */
export { ConfiguratorCanvas };
